import { useState, useRef, useMemo, useEffect } from "react";

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONT = `@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Bebas+Neue&family=Audiowide&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');`;

// ─── API LAYER ────────────────────────────────────────────────────────────────
const API_BASE = "https://kascomm.uk";
let _token = null; // in-memory JWT store

const api = {
  setToken:(t)=>{ _token=t; },
  clearToken:()=>{ _token=null; },

  async fetch(path, opts={}){
    const headers = { "Content-Type":"application/json", ...(opts.headers||{}) };
    if(_token) headers["Authorization"] = `Bearer ${_token}`;
    const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
    if(res.status===401){ api.clearToken(); return null; }
    if(!res.ok){ const e=await res.json().catch(()=>({error:res.statusText})); throw new Error(e.error||res.statusText); }
    return res.json();
  },

  get:(path)=>api.fetch(path),
  post:(path,body)=>api.fetch(path,{method:"POST",body:JSON.stringify(body)}),
  patch:(path,body)=>api.fetch(path,{method:"PATCH",body:JSON.stringify(body)}),
  del:(path)=>api.fetch(path,{method:"DELETE"}),
};

// ─── DATA MAPPERS (snake_case API ↔ camelCase UI) ────────────────────────────
// Unix timestamp (seconds) → "YYYY-MM-DD" string, or null
const tsToDate = ts => { if(!ts) return ""; const d=new Date(ts*1000); const y=d.getFullYear(); const m=String(d.getMonth()+1).padStart(2,"0"); const day=String(d.getDate()).padStart(2,"0"); return `${y}-${m}-${day}`; };

// "YYYY-MM-DD" string → Unix timestamp (seconds), or null
const dateToTs = s => s ? Math.floor(new Date(s+"T00:00:00").getTime()/1000) : null;

// Status: API uses underscores, UI uses hyphens
const toUiStatus  = s => s ? s.replace(/_/g,"-") : "not-started";
const toApiStatus = s => s ? s.replace(/-/g,"_") : "not_started";

function mapUser(u){
  return {
    id:          u.id,
    name:        u.name,
    email:       u.email,
    role:        u.role,
    color:       u.avatar_colour || "#ff6eb4",
    initials:    u.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(),
  };
}

function mapProject(p){
  return {
    id:          p.id,
    name:        p.name,
    description: p.description || "",
    status:      toUiStatus(p.status),
    ownerId:     p.owner_id,
    color:       p.colour || "#ff6eb4",
    taskCount:   p.task_count || 0,
    completeCount: p.complete_count || 0,
  };
}

function mapTask(t){
  if(!t||t.id==null) return null;
  return {
    id:          t.id,
    projectId:   t.project_id,
    name:        t.name,
    notes:       t.description || "",
    startDate:   tsToDate(t.planned_start),
    finishDate:  tsToDate(t.planned_finish),
    assigneeId:  t.assignee_id,
    status:      toUiStatus(t.status),
    repeatable:  !!t.is_repeatable,
    frequency:   t.repeat_interval || "monthly",
    comments:    [],  // loaded separately when modal opens
    // predecessor IDs come from the project detail endpoint
    predecessorIds: t.predecessor_ids || [],
  };
}

function mapComment(c){
  return {
    id:        c.id,
    userId:    c.author_id,
    text:      c.body,
    timestamp: typeof c.created_at === "number"
      ? new Date(c.created_at*1000).toISOString()
      : c.created_at,
    authorName:   c.author_name,
    authorColour: c.author_colour,
  };
}

// Build deps array from predecessor_ids on tasks (no separate deps table in UI)
function buildDepsFromTasks(tasks){
  const deps=[];
  let did=1;
  tasks.forEach(t=>{
    (t.predecessorIds||[]).forEach(predId=>{
      deps.push({id:"d"+did++, predecessorId:predId, successorId:t.id, lagDays:0});
    });
  });
  return deps;
}

// ─── DATE HELPERS ─────────────────────────────────────────────────────────────
const parseDate  = s => new Date(s+"T00:00:00");
const fmtDate    = d => d instanceof Date ? d.toISOString().slice(0,10) : d;
const fmtDisplay = d => { const x=d instanceof Date?d:parseDate(d); return x.toLocaleDateString("en-GB",{day:"numeric",month:"short"}); };
const addDays    = (d,n) => { const x=new Date(d); x.setDate(x.getDate()+n); return x; };
const diffDays   = (a,b) => Math.round((b-a)/86400000);
const durDays    = t => diffDays(parseDate(t.startDate),parseDate(t.finishDate));

// ─── IMPLIED DATE ENGINE ──────────────────────────────────────────────────────
function computeImplied(tasks,deps){
  const tm=Object.fromEntries(tasks.map(t=>[t.id,t]));
  const implied={};
  const inDeg=Object.fromEntries(tasks.map(t=>[t.id,0]));
  const succ=Object.fromEntries(tasks.map(t=>[t.id,[]]));
  deps.forEach(d=>{if(tm[d.predecessorId]&&tm[d.successorId]){inDeg[d.successorId]++;succ[d.predecessorId].push(d);}});
  const q=tasks.filter(t=>!inDeg[t.id]).map(t=>t.id); const order=[];
  while(q.length){const id=q.shift();order.push(id);succ[id].forEach(d=>{inDeg[d.successorId]--;if(!inDeg[d.successorId])q.push(d.successorId);});}
  order.forEach(id=>{
    const t=tm[id]; if(!t)return;
    const dur=durDays(t); const ps=parseDate(t.startDate);
    const preds=deps.filter(d=>d.successorId===id&&tm[d.predecessorId]);
    let late=null;
    preds.forEach(d=>{const pi=implied[d.predecessorId];if(pi){const c=addDays(pi.impliedFinish,d.lagDays);if(!late||c>late)late=c;}});
    const iS=late&&late>ps?late:ps;
    implied[id]={impliedStart:iS,impliedFinish:addDays(iS,dur)};
  });
  return implied;
}

function computeCriticalPath(tasks,deps,implied){
  if(!tasks.length)return new Set();
  const tm=Object.fromEntries(tasks.map(t=>[t.id,t]));
  let projEnd=null;
  tasks.forEach(t=>{const i=implied[t.id];if(i&&(!projEnd||i.impliedFinish>projEnd))projEnd=i.impliedFinish;});
  if(!projEnd)return new Set();
  const lF=Object.fromEntries(tasks.map(t=>[t.id,projEnd]));
  const lS={};
  const succ=Object.fromEntries(tasks.map(t=>[t.id,[]]));
  deps.forEach(d=>{if(tm[d.predecessorId]&&tm[d.successorId])succ[d.predecessorId].push(d);});
  const inDeg=Object.fromEntries(tasks.map(t=>[t.id,0]));
  deps.forEach(d=>{if(tm[d.successorId])inDeg[d.successorId]++;});
  const q=[...tasks.filter(t=>!inDeg[t.id]).map(t=>t.id)]; const ord=[]; const idc={...inDeg};
  while(q.length){const id=q.shift();ord.push(id);succ[id].forEach(d=>{idc[d.successorId]--;if(!idc[d.successorId])q.push(d.successorId);});}
  [...ord].reverse().forEach(id=>{
    const t=tm[id];if(!t)return;
    const dur=durDays(t); lS[id]=addDays(lF[id],-dur);
    succ[id].forEach(d=>{const sl=lS[d.successorId];if(sl){const lf=addDays(sl,-d.lagDays);if(lf<lF[id]){lF[id]=lf;lS[id]=addDays(lF[id],-dur);}}});
  });
  const cp=new Set();
  tasks.forEach(t=>{const i=implied[t.id];if(!i)return;if(diffDays(i.impliedFinish,lF[t.id])<=0)cp.add(t.id);});
  return cp;
}

// ─── MIAMI VICE CSS ───────────────────────────────────────────────────────────
const CSS = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --bg:#0a0a1a;
  --surface:#10102a;
  --surface2:#181838;
  --surface3:#1e1e42;
  --border:#ff6eb418;
  --border2:#ff6eb438;
  --pink:#ff6eb4;
  --teal:#40e0d0;
  --purple:#b06eff;
  --accent:var(--pink);
  --accent2:var(--teal);
  --danger:#ff4466;
  --success:#40e0d0;
  --warning:#ffaa55;
  --cp:#ff4d4d;
  --text:#ffe8f4;
  --muted:#7766aa;
  --muted2:#bbaacc;
  --radius:10px;
  --radius-lg:18px;
  --gantt-row:38px;
  --gantt-header:44px;
  /* ── TYPOGRAPHY RULES ── */
  --font-ui:'Orbitron',sans-serif;       /* logo, nav, stats, labels, buttons */
  --font-content:'Bebas Neue',sans-serif; /* project names, task names */
  --font-body:'DM Sans',sans-serif;       /* descriptions, meta, body */
}

html,body{background:#0a0a1a!important;color:var(--text);font-family:var(--font-body);min-height:100vh;overflow-x:hidden;}
#root,#app{background:#0a0a1a;min-height:100vh;}

/* Global heading override — UI elements use Orbitron */
h1,h2,h3,h4{font-family:var(--font-ui);}

/* Content titles — Bebas Neue */
.content-title{font-family:var(--font-content)!important;letter-spacing:.04em;cursor:pointer;}
.task-name-link{transition:color .15s;}
.task-name-link:hover{color:var(--pink);}

::-webkit-scrollbar{width:4px;height:4px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:var(--border2);border-radius:4px;}

@keyframes fadeUp{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes miamGlow{0%,100%{box-shadow:0 0 20px rgba(255,110,180,.15);}50%{box-shadow:0 0 30px rgba(255,110,180,.3);}}
.fade-up{animation:fadeUp .3s ease both;}
.fade-in{animation:fadeIn .2s ease both;}

/* ── LAYOUT ── */
.shell{display:flex;flex-direction:column;min-height:100vh;background:#0a0a1a;}

/* ── TOPBAR ── */
.topbar{
  display:flex;align-items:center;gap:12px;padding:0 24px;height:54px;
  background:rgba(10,10,26,.94);backdrop-filter:blur(14px);
  border-bottom:1px solid var(--border2);
  position:sticky;top:0;z-index:200;
}
.topbar::after{content:'';position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--pink),var(--teal),transparent);}
.topbar-sep{width:1px;height:20px;background:linear-gradient(180deg,transparent,var(--purple),transparent);}
.topbar-sub{font-family:var(--font-ui);font-size:10px;letter-spacing:.1em;color:var(--muted);}

/* ── KASCOMM LOGO — INLINE (topbar) ── */
.logo-inline{display:flex;align-items:center;gap:0;flex-shrink:0;margin-right:8px;}
.logo-inline-kascomm{font-family:'Audiowide',sans-serif;font-size:13px;letter-spacing:.1em;text-transform:uppercase;background:linear-gradient(135deg,#ffffff 0%,#ff9fd4 40%,#b06eff 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;filter:drop-shadow(0 0 6px rgba(255,110,180,.4));}
.logo-inline-sep{font-family:'Audiowide',sans-serif;font-size:11px;color:#40e0d0;margin:0 5px;opacity:.7;}
.logo-inline-hub{font-family:'Audiowide',sans-serif;font-size:13px;letter-spacing:.1em;text-transform:uppercase;color:#40e0d0;text-shadow:0 0 10px rgba(64,224,208,.5);}

/* ── KASCOMM LOGO — STACKED (login + sidebar) ── */
.logo-stacked{display:flex;flex-direction:column;align-items:center;gap:0;background:linear-gradient(180deg,#1a0038 0%,#0a0a1a 100%);border-radius:10px;border:1px solid #b06eff44;padding:14px 20px 10px;position:relative;overflow:hidden;box-shadow:0 0 24px rgba(176,110,255,.15);}
.logo-stacked::before{content:'';position:absolute;bottom:0;left:0;right:0;height:55%;background:repeating-linear-gradient(180deg,transparent,transparent 10px,rgba(176,110,255,.1) 10px,rgba(176,110,255,.1) 11px),repeating-linear-gradient(90deg,transparent,transparent 20px,rgba(176,110,255,.06) 20px,rgba(176,110,255,.06) 21px);pointer-events:none;}
.logo-stacked-kascomm{font-family:'Audiowide',sans-serif;letter-spacing:.1em;text-transform:uppercase;color:#ffffff;position:relative;z-index:1;line-height:1;text-shadow:0 0 18px rgba(176,110,255,.9),0 3px 0 #330066;}
.logo-stacked-hub{font-family:'Audiowide',sans-serif;letter-spacing:.4em;text-transform:uppercase;color:#b06eff;position:relative;z-index:1;text-shadow:0 0 10px #b06eff;}
.logo-stacked-horizon{width:100%;height:2px;position:relative;z-index:1;background:linear-gradient(90deg,transparent,#b06eff,#ff6eb4,#b06eff,transparent);box-shadow:0 0 8px rgba(176,110,255,.5);}
.logo-stacked-tagline{font-family:'Orbitron',sans-serif;letter-spacing:.16em;text-transform:uppercase;color:#40e0d0;opacity:.7;position:relative;z-index:1;}

/* ── SIDEBAR LOGO HEADER ── */
.sidebar-logo-wrap{padding:12px 12px 14px;border-bottom:1px solid var(--border2);margin-bottom:8px;}

.main{display:flex;flex:1;overflow:hidden;}

/* ── SIDEBAR ── */
.sidebar{
  width:220px;flex-shrink:0;
  background:linear-gradient(180deg,var(--surface) 0%,rgba(10,10,26,.98) 100%);
  border-right:1px solid var(--border2);
  display:flex;flex-direction:column;padding:0 0 16px;overflow-y:auto;
}
.nav-section{padding:0 12px;margin-bottom:4px;}
.nav-label{font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);padding:8px 8px 4px;}
.nav-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:8px;cursor:pointer;font-family:var(--font-ui);font-size:10px;font-weight:600;letter-spacing:.06em;color:var(--muted2);transition:all .15s;border:none;background:transparent;width:100%;text-align:left;}
.nav-item:hover{background:var(--surface2);color:var(--text);}
.nav-item.active{background:var(--surface3);color:var(--pink);border-left:2px solid var(--pink);}
.nav-item .dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.nav-icon{font-size:14px;width:18px;text-align:center;}

/* ── CONTENT ── */
.content{flex:1;overflow:auto;padding:28px 32px 40px;background:var(--bg);}
.page-hdr{display:flex;align-items:center;gap:14px;margin-bottom:24px;}
.page-hdr h2{font-size:20px;font-weight:900;letter-spacing:.08em;}
.spacer{flex:1;}

/* ── BUTTONS ── */
.btn{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:8px;border:none;font-family:var(--font-ui);font-size:11px;font-weight:700;letter-spacing:.08em;cursor:pointer;transition:all .18s;white-space:nowrap;text-transform:uppercase;}
.btn-primary{background:linear-gradient(135deg,var(--pink),#ff4499);color:#0a0a1a;box-shadow:0 4px 15px rgba(255,110,180,.35);}
.btn-primary:hover{box-shadow:0 6px 22px rgba(255,110,180,.55);transform:translateY(-1px);}
.btn-ghost{background:var(--surface2);color:var(--muted2);border:1px solid var(--border2);}
.btn-ghost:hover{color:var(--text);border-color:var(--pink);}
.btn-ghost.active{background:var(--surface3);color:var(--pink);border-color:var(--pink);}
.btn-danger{background:rgba(255,68,102,.12);color:var(--danger);border:1px solid rgba(255,68,102,.25);}
.btn-danger:hover{background:rgba(255,68,102,.22);}
.btn-sm{padding:5px 11px;font-size:10px;}
.btn-cp{background:rgba(255,77,77,.12);color:var(--cp);border:1px solid rgba(255,77,77,.25);}
.btn-cp.active{background:rgba(255,77,77,.25);border-color:var(--cp);}

/* ── CARDS ── */
.card{
  background:var(--surface);
  border:1px solid var(--border);
  border-left:3px solid var(--pink);
  border-radius:var(--radius-lg);
  padding:20px;margin-bottom:14px;
  transition:border-color .2s,box-shadow .2s;
}
.card:hover{border-color:var(--border2);box-shadow:0 4px 24px rgba(255,110,180,.1);}
.card.teal-left{border-left-color:var(--teal);}
.card.purple-left{border-left-color:var(--purple);}

/* ── STAT GRID ── */
.stat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px;}
.stat-card{
  background:var(--surface);border:1px solid var(--border);
  border-radius:var(--radius);padding:16px 18px;
  transition:box-shadow .2s;
}
.stat-card:hover{box-shadow:0 4px 20px rgba(255,110,180,.1);}
.stat-val{font-family:var(--font-ui);font-size:32px;font-weight:900;line-height:1;}
.stat-lbl{font-family:var(--font-ui);font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);margin-top:5px;}

/* ── BADGES ── */
.badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;}
.badge-not-started{background:rgba(176,110,255,.15);color:var(--purple);}
.badge-in-progress{background:rgba(64,224,208,.15);color:var(--teal);}
.badge-stuck      {background:rgba(255,170,85,.15);color:var(--warning);}
.badge-complete   {background:rgba(119,102,170,.12);color:var(--muted);}

/* ── TASK STATUS PILL ── */
.task-status-pill{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;border:none;transition:all .15s;}
.task-status-pill:hover{filter:brightness(1.2);}

/* ── AVATAR COLOUR PICKER ── */
.avatar-picker{display:flex;flex-wrap:wrap;gap:7px;margin-top:10px;}
.avatar-swatch{width:26px;height:26px;border-radius:50%;cursor:pointer;border:2px solid transparent;transition:all .15s;flex-shrink:0;}
.avatar-swatch:hover{transform:scale(1.15);}
.avatar-swatch.active{border-color:#ffffff;box-shadow:0 0 10px var(--sw-col);}

/* ── PROGRESS ── */
.prog-bar{height:5px;background:var(--surface2);border-radius:3px;overflow:hidden;}
.prog-fill{height:100%;border-radius:3px;transition:width .5s ease;}
.prog-fill-gradient{background:linear-gradient(90deg,var(--pink),var(--teal))!important;}

/* ── TABLE ── */
.tbl{width:100%;border-collapse:collapse;font-size:13px;}
.tbl th{font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);padding:8px 12px;border-bottom:1px solid var(--border2);text-align:left;white-space:nowrap;}
.tbl td{padding:9px 12px;border-bottom:1px solid var(--border);vertical-align:middle;}
.tbl tr:last-child td{border-bottom:none;}
.tbl tr:hover td{background:rgba(255,110,180,.03);}
.tbl .done-row td{opacity:.45;}
.implied-badge{font-size:11px;color:var(--warning);font-weight:600;}
.cp-badge{display:inline-block;width:8px;height:8px;border-radius:50%;background:var(--cp);flex-shrink:0;}

/* ── GANTT ── */
.gantt-wrap{display:flex;flex-direction:column;background:var(--surface);border:1px solid var(--border2);border-radius:var(--radius-lg);overflow:hidden;}
.gantt-toolbar{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--border2);flex-wrap:wrap;background:var(--surface);}
.gantt-body{display:flex;overflow:hidden;}
.gantt-labels{flex-shrink:0;width:220px;border-right:1px solid var(--border2);overflow:hidden;}
.gantt-labels-header{height:var(--gantt-header);display:flex;align-items:center;padding:0 14px;border-bottom:1px solid var(--border2);font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);}
.gantt-label-row{height:var(--gantt-row);display:flex;align-items:center;padding:0 14px;border-bottom:1px solid var(--border);gap:8px;}
.gantt-label-row:hover{background:rgba(255,110,180,.04);}
.gantt-label-text{font-family:var(--font-content);font-size:14px;letter-spacing:.03em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;}
.gantt-chart{flex:1;overflow-x:auto;position:relative;}
.gantt-canvas-wrap{position:relative;min-width:100%;}
.gantt-header-row{height:var(--gantt-header);display:flex;align-items:flex-end;border-bottom:1px solid var(--border2);position:sticky;top:0;z-index:10;background:var(--surface);}
.gantt-col-label{position:absolute;bottom:8px;font-family:var(--font-ui);font-size:9px;font-weight:600;color:var(--muted);letter-spacing:.06em;transform:translateX(-50%);white-space:nowrap;}
.gantt-grid-line{position:absolute;top:0;bottom:0;width:1px;background:var(--border);opacity:.5;}
.gantt-today-line{position:absolute;top:0;bottom:0;width:2px;background:linear-gradient(180deg,var(--pink),var(--teal));opacity:.8;z-index:5;}
.gantt-task-row{height:var(--gantt-row);position:relative;border-bottom:1px solid var(--border);}
.gantt-task-row:hover{background:rgba(255,110,180,.03);}
.gantt-bar{position:absolute;top:50%;height:16px;border-radius:4px;transform:translateY(-50%);transition:all .3s ease;}
.gantt-bar-planned{opacity:.22;}
.gantt-bar-implied{opacity:.9;cursor:pointer;}
.gantt-bar-implied:hover{opacity:1;filter:brightness(1.15);}
.gantt-bar-cp{box-shadow:0 0 10px rgba(255,77,77,.7);}
.gantt-lag{position:absolute;top:50%;height:2px;transform:translateY(-50%);background:repeating-linear-gradient(90deg,var(--warning) 0,var(--warning) 4px,transparent 4px,transparent 8px);opacity:.7;}

/* ── MODAL ── */
.overlay{position:fixed;inset:0;background:rgba(0,0,0,.75);backdrop-filter:blur(6px);z-index:400;display:flex;align-items:center;justify-content:center;padding:24px;animation:fadeIn .2s ease;}
.modal{background:var(--surface);border:1px solid var(--border2);border-top:2px solid var(--pink);border-radius:var(--radius-lg);padding:28px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.6),0 0 40px rgba(255,110,180,.1);}
.modal-wide{max-width:880px;}
.modal h3{font-family:var(--font-ui);font-size:16px;font-weight:900;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px;color:var(--pink);}
.edit-modal-body{display:flex;gap:24px;}
.edit-modal-left{flex:1;min-width:0;}
.edit-modal-right{width:300px;flex-shrink:0;display:flex;flex-direction:column;border-left:1px solid var(--border2);padding-left:24px;}
@media(max-width:900px){.edit-modal-body{flex-direction:column;}.edit-modal-right{width:100%;border-left:none;padding-left:0;border-top:1px solid var(--border2);padding-top:18px;}.modal-wide{max-width:500px;}}
.field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px;}
.field label{font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);}
.field input,.field select,.field textarea{background:var(--surface2);border:1px solid var(--border2);border-radius:8px;padding:9px 12px;color:var(--text);font-family:var(--font-body);font-size:16px;outline:none;transition:border-color .2s;width:100%;}
.field input:focus,.field select:focus,.field textarea:focus{border-color:var(--pink);box-shadow:0 0 0 3px rgba(255,110,180,.1);}
.field textarea{resize:vertical;min-height:70px;}
.row-fields{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.modal-foot{display:flex;gap:10px;margin-top:20px;justify-content:flex-end;}
.dep-item{display:flex;align-items:center;gap:8px;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:8px 12px;margin-bottom:8px;font-size:13px;}
.dep-item .lag-input{width:60px;background:var(--surface);border:1px solid var(--border2);border-radius:6px;padding:4px 7px;color:var(--text);font-size:12px;font-family:var(--font-body);}
.dep-remove{margin-left:auto;background:none;border:none;color:var(--muted);cursor:pointer;font-size:16px;}
.dep-remove:hover{color:var(--danger);}

/* ── PHOSPHOR FADE ── */
@keyframes phosphorIn{
  0%{opacity:0;filter:brightness(4) saturate(0) blur(3px);}
  40%{opacity:1;filter:brightness(2.5) saturate(.4) blur(0);}
  70%{filter:brightness(1.4) saturate(.7);}
  100%{opacity:1;filter:brightness(1) saturate(1);}
}
@keyframes phosphorBloom{0%{opacity:.5;}60%{opacity:.15;}100%{opacity:0;}}
.phosphor-item-1{animation:phosphorIn .6s .05s ease both;}
.phosphor-item-2{animation:phosphorIn .6s .2s ease both;}
.phosphor-item-3{animation:phosphorIn .6s .35s ease both;}
.phosphor-item-4{animation:phosphorIn .6s .5s ease both;}
.phosphor-item-5{animation:phosphorIn .6s .65s ease both;}
.phosphor-card{animation:phosphorIn .5s ease both;}
.phosphor-card:nth-child(1){animation-delay:.05s;}
.phosphor-card:nth-child(2){animation-delay:.15s;}
.phosphor-card:nth-child(3){animation-delay:.25s;}
.phosphor-card:nth-child(4){animation-delay:.35s;}
.phosphor-card:nth-child(5){animation-delay:.42s;}
.phosphor-card:nth-child(6){animation-delay:.49s;}
.phosphor-card:nth-child(7){animation-delay:.54s;}
.phosphor-card:nth-child(8){animation-delay:.59s;}
.phosphor-card:nth-child(9){animation-delay:.63s;}
.phosphor-card:nth-child(10){animation-delay:.67s;}
.phosphor-card:nth-child(n+11){animation-delay:.7s;}
.phosphor-bloom{position:fixed;inset:0;pointer-events:none;z-index:999;background:radial-gradient(ellipse 60% 40% at 50% 30%,rgba(64,224,208,.1) 0%,transparent 70%);animation:phosphorBloom 1.4s ease forwards;}

/* ── OUTRUN LOGIN ── */
.login-wrap{
  min-height:100vh;position:relative;overflow:hidden;
  display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
  padding:0;
  background:#080018;
}
/* Sky */
.outrun-sky{
  position:absolute;top:0;left:0;right:0;height:52%;
  background:linear-gradient(180deg,#080018 0%,#1a0040 60%,#2a0060 100%);
  z-index:0;
}
.outrun-sky-glow{
  position:absolute;top:0;left:0;right:0;height:52%;
  background:radial-gradient(ellipse 100% 80% at 50% 100%,rgba(176,110,255,.35) 0%,rgba(255,110,180,.15) 50%,transparent 75%);
  z-index:1;
}
/* Sun */
.outrun-sun{
  position:absolute;
  width:min(200px,50vw);
  padding-bottom:min(100px,25vw);
  left:50%;transform:translateX(-50%);
  top:calc(52% - min(100px,25vw));
  background:linear-gradient(180deg,#ff6eb4 0%,#ff9fd4 35%,#ffccee 70%,#ffe8f4 100%);
  border-radius:50% 50% 0 0 / 100% 100% 0 0;
  box-shadow:0 0 60px rgba(255,110,180,.7),0 0 120px rgba(255,110,180,.35);
  overflow:hidden;z-index:2;
}
.outrun-sun-stripes{
  position:absolute;inset:0;
  background:repeating-linear-gradient(180deg,transparent,transparent 10%,rgba(10,0,24,.45) 10%,rgba(10,0,24,.45) 13%);
}
/* Horizon */
.outrun-horizon{
  position:absolute;top:52%;left:0;right:0;height:3px;
  background:linear-gradient(90deg,transparent,#b06eff 15%,#ff6eb4 50%,#b06eff 85%,transparent);
  box-shadow:0 0 20px rgba(176,110,255,.9),0 0 40px rgba(255,110,180,.5),0 0 80px rgba(176,110,255,.3);
  z-index:3;
}
/* Floor */
.outrun-floor{
  position:absolute;top:52%;left:0;right:0;bottom:0;
  background:linear-gradient(180deg,#0a0018 0%,#0a0a1a 100%);
  z-index:0;overflow:hidden;
}
.outrun-grid-svg{position:absolute;top:0;left:0;width:100%;height:100%;}

/* Logo in sky */
.outrun-logo{
  position:relative;z-index:5;
  display:flex;flex-direction:column;align-items:center;
  padding:clamp(24px,5vw,48px) 0 0;
  width:100%;
}
.outrun-kascomm{
  font-family:'Audiowide',sans-serif;
  font-size:clamp(24px,6vw,42px);
  letter-spacing:.12em;text-transform:uppercase;
  color:#ffffff;line-height:1;
  text-shadow:0 0 30px rgba(176,110,255,1),0 0 60px rgba(176,110,255,.6),0 6px 0 #220044;
}
.outrun-rule{
  width:clamp(200px,50%,360px);height:2px;margin:12px 0;
  background:linear-gradient(90deg,transparent,#b06eff,#ff6eb4,#b06eff,transparent);
  box-shadow:0 0 12px rgba(176,110,255,.7);
}
.outrun-hub{
  font-family:'Audiowide',sans-serif;
  font-size:clamp(10px,2vw,14px);
  letter-spacing:.5em;text-transform:uppercase;
  color:#b06eff;text-shadow:0 0 14px #b06eff,0 0 28px rgba(176,110,255,.5);
}
.outrun-tagline{
  font-family:'Orbitron',sans-serif;font-size:clamp(7px,1.2vw,10px);
  letter-spacing:.22em;text-transform:uppercase;
  color:#40e0d0;opacity:.8;margin-top:10px;
  text-shadow:0 0 10px rgba(64,224,208,.5);
}

/* Floating login card */
.login-card-wrap{
  position:relative;z-index:5;
  width:100%;max-width:420px;
  padding:0 20px clamp(24px,4vw,40px);
  margin-top:clamp(8px,2vw,20px);
}
.login-card{
  background:rgba(16,16,42,.88);
  border:1px solid #ff6eb444;
  border-top:2px solid var(--pink);
  border-radius:var(--radius-lg);
  padding:28px 28px 24px;
  backdrop-filter:blur(16px);
  box-shadow:0 -4px 30px rgba(0,0,0,.3),0 20px 60px rgba(0,0,0,.7),0 0 40px rgba(255,110,180,.08);
}
.login-card h1{font-family:var(--font-ui);font-size:18px;font-weight:900;letter-spacing:.1em;margin-bottom:4px;color:var(--text);}
.login-card .sub{font-family:var(--font-body);font-size:13px;color:var(--muted);margin-bottom:22px;}
.user-chips{display:flex;flex-direction:column;gap:7px;margin-top:18px;}
.user-chips p{font-family:var(--font-ui);font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);text-align:center;margin-bottom:3px;}
.chip{display:flex;align-items:center;gap:10px;background:rgba(24,24,56,.7);border:1px solid var(--border);border-radius:8px;padding:9px 14px;cursor:pointer;transition:all .18s;}
.chip:hover{border-color:var(--pink);box-shadow:0 0 12px rgba(255,110,180,.2);}
.av{border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--font-ui);font-weight:800;flex-shrink:0;}
.err{font-family:var(--font-body);color:var(--danger);font-size:12px;margin-top:6px;}

/* ── SIDEBAR OUTRUN HEADER ── */
.sidebar-outrun{
  position:relative;overflow:hidden;height:110px;flex-shrink:0;
  border-bottom:1px solid var(--border2);
  background:#080018;
}
.sb-sky{position:absolute;inset:0;background:linear-gradient(180deg,#080018 0%,#1a0040 100%);}
.sb-sky-glow{position:absolute;inset:0;background:radial-gradient(ellipse 140% 100% at 50% 110%,rgba(176,110,255,.3) 0%,transparent 65%);}
.sb-sun{
  position:absolute;width:72px;padding-bottom:36px;
  left:50%;transform:translateX(-50%);
  top:calc(62% - 36px);
  background:linear-gradient(180deg,#ff6eb4 0%,#ffccee 100%);
  border-radius:50% 50% 0 0 / 100% 100% 0 0;
  box-shadow:0 0 24px rgba(255,110,180,.7);
  overflow:hidden;z-index:2;
}
.sb-sun-stripes{position:absolute;inset:0;background:repeating-linear-gradient(180deg,transparent,transparent 18%,rgba(10,0,24,.45) 18%,rgba(10,0,24,.45) 24%);}
.sb-horizon{position:absolute;top:62%;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#b06eff,#ff6eb4,#b06eff,transparent);box-shadow:0 0 12px rgba(176,110,255,.8);z-index:3;}
.sb-floor{position:absolute;top:62%;left:0;right:0;bottom:0;background:#08001a;z-index:0;overflow:hidden;}
.sb-grid-svg{position:absolute;top:0;left:0;width:100%;height:100%;}
.sb-logo{position:absolute;top:0;left:0;right:0;z-index:5;display:flex;flex-direction:column;align-items:center;padding-top:12px;}
.sb-kascomm{font-family:'Audiowide',sans-serif;font-size:15px;letter-spacing:.1em;text-transform:uppercase;color:#fff;text-shadow:0 0 16px rgba(176,110,255,.9),0 3px 0 #220044;}
.sb-rule{width:80%;height:1px;margin:5px 0;background:linear-gradient(90deg,transparent,#b06eff,#ff6eb4,#b06eff,transparent);box-shadow:0 0 8px rgba(176,110,255,.6);}
.sb-hub{font-family:'Audiowide',sans-serif;font-size:8px;letter-spacing:.4em;text-transform:uppercase;color:#b06eff;text-shadow:0 0 10px #b06eff;}

/* ── MOBILE ── */
@media(max-width:900px){
  .sidebar{display:none;}
  .content{padding:18px 16px 90px;}
  .stat-grid{grid-template-columns:1fr 1fr;}
  .mobile-nav{display:flex!important;}
  .gantt-section{display:none!important;}
  .task-col-dates{display:none!important;}
  .task-row-desktop{grid-template-columns:minmax(0,1fr) 32px 90px 44px!important;gap:0 6px!important;}
  .edit-modal-body{flex-direction:column!important;}
  .edit-modal-right{width:100%!important;border-left:none!important;padding-left:0!important;border-top:1px solid var(--border2);padding-top:18px!important;}
  .modal-wide{max-width:96vw!important;}
}
/* ── DESKTOP ── */
@media(min-width:900px){
  .content{padding:28px 32px 40px;}
  .content-title{font-size:18px!important;}
  .page-hdr h2{font-size:26px!important;}
  .stat-card .stat-val{font-size:32px!important;}
  .stat-card .stat-lbl{font-size:10px!important;}
  .card{padding:18px 20px;}
  .task-row-desktop{grid-template-columns:500px 32px 70px 70px 70px 70px 100px 44px!important;gap:0 8px!important;}
  .task-col-dates{display:contents;}
  .edit-modal-mobile-tabs{display:none!important;}
  .edit-modal-left{display:block!important;}
  .edit-modal-right{display:flex!important;}
}
.mobile-nav{display:none;position:fixed;bottom:0;left:0;right:0;background:rgba(10,10,26,.97);backdrop-filter:blur(12px);border-top:1px solid var(--border2);z-index:300;}
.mobile-nav::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--pink),var(--teal),transparent);}
.mob-nav-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:9px 0 12px;border:none;background:transparent;color:var(--muted);font-family:var(--font-ui);font-size:8px;letter-spacing:.06em;cursor:pointer;transition:color .15s;}
.mob-nav-item.active{color:var(--pink);}
.mob-icon{font-size:17px;}

/* ── EMPTY STATE ── */
.empty-state{
  position:relative;overflow:hidden;
  border-radius:var(--radius-lg);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  padding:60px 32px;text-align:center;
  background:#080018;
  border:1px solid var(--border2);
}
.empty-grid{position:absolute;bottom:0;left:0;right:0;height:55%;overflow:hidden;}
.empty-grid-svg{width:100%;height:100%;}
.empty-horizon{position:absolute;bottom:44%;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#b06eff 20%,#ff6eb4 50%,#b06eff 80%,transparent);box-shadow:0 0 14px rgba(176,110,255,.7),0 0 28px rgba(255,110,180,.3);}
.empty-sky-glow{position:absolute;top:0;left:0;right:0;bottom:44%;background:radial-gradient(ellipse 100% 80% at 50% 100%,rgba(176,110,255,.2) 0%,rgba(255,110,180,.08) 50%,transparent 75%);}
.empty-message{position:relative;z-index:2;font-family:'Audiowide',sans-serif;font-size:clamp(13px,2vw,16px);color:#ffffff;letter-spacing:.06em;text-shadow:0 0 20px rgba(176,110,255,.6);line-height:1.5;max-width:340px;}
.empty-sub{position:relative;z-index:2;font-family:'Orbitron',sans-serif;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#40e0d0;opacity:.7;margin-top:10px;}

/* ── COMMENTS ── */
.comment-log{display:flex;flex-direction:column;gap:8px;margin-bottom:14px;max-height:280px;overflow-y:auto;}
.comment-log::-webkit-scrollbar{width:3px;}
.comment-log::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px;}
.comment-item{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:10px 12px;}
.comment-meta{display:flex;align-items:center;gap:8px;margin-bottom:5px;}
.comment-av{width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--font-ui);font-size:8px;font-weight:800;flex-shrink:0;}
.comment-who{font-family:var(--font-ui);font-size:9px;font-weight:700;letter-spacing:.08em;color:var(--muted2);}
.comment-date{font-family:var(--font-ui);font-size:9px;color:var(--muted);letter-spacing:.06em;}
.comment-sep{font-size:10px;color:var(--border2);}
.comment-text{font-family:var(--font-body);font-size:13px;color:var(--text);line-height:1.5;}
.comment-input-row{display:flex;gap:8px;align-items:flex-end;}
.comment-input-row textarea{flex:1;min-height:60px;background:var(--surface2);border:1px solid var(--border2);border-radius:8px;padding:9px 12px;color:var(--text);font-family:var(--font-body);font-size:13px;outline:none;resize:none;transition:border-color .2s;}
.comment-input-row textarea:focus{border-color:var(--pink);}
`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
// Project status
const projStatusClass = {"not-started":"badge-not-started","in-progress":"badge-in-progress","stuck":"badge-stuck","complete":"badge-complete"};
const projStatusLabel = {"not-started":"Not Started","in-progress":"In Progress","stuck":"Stuck","complete":"Complete"};
// Task status (same system)
const TASK_STATUSES = ["not-started","in-progress","stuck","complete"];
const taskStatusLabel = {"not-started":"Not Started","in-progress":"In Progress","stuck":"Stuck","complete":"Complete"};
const taskStatusColor = {"not-started":"var(--purple)","in-progress":"var(--teal)","stuck":"var(--warning)","complete":"var(--muted)"};
const taskStatusBg    = {"not-started":"rgba(176,110,255,.15)","in-progress":"rgba(64,224,208,.15)","stuck":"rgba(255,170,85,.15)","complete":"rgba(119,102,170,.12)"};
// Miami project colours palette
const PROJ_COLORS = ["#ff6eb4","#40e0d0","#b06eff","#ff6b6b","#ffaa55","#00ccff","#da70d6","#aaff00","#cc2266","#88eeff"];
// Avatar palette (same set)
const AVATAR_COLORS = PROJ_COLORS;
const cardAccentClass = (i) => i%3===0?"":i%3===1?"teal-left":"purple-left";
const progColor = (p,col) => col || (p===100?"var(--teal)":p>50?"var(--pink)":"var(--teal)");
const uid = (()=>{let n=500;return()=>++n;})();
const fmtComment = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString("en-GB",{month:"short",day:"2-digit"}).replace(" ","-");
};
const addDaysPlain=(d,n)=>{const x=new Date(d);x.setDate(x.getDate()+n);return x;};
const fmtDatePlain=(d)=>d.toISOString().slice(0,10);

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App(){
  const [user,setUser]         = useState(null);
  const [users,setUsers]       = useState([]);
  const [projects,setProjects] = useState([]);
  const [tasks,setTasks]       = useState([]);
  const [deps,setDeps]         = useState([]);
  const [loading,setLoading]   = useState(false);
  const [apiErr,setApiErr]     = useState("");
  const [page,setPage]         = useState("dashboard");
  const [bloom,setBloom]       = useState(true);
  const [selProj,setSelProj]   = useState(null);
  const [modal,setModal]       = useState(null);
  const [loginErr,setLoginErr] = useState("");
  const emailRef = useRef();
  const passRef  = useRef();

  // Clear initial bloom on first mount
  useEffect(()=>{const t=setTimeout(()=>setBloom(false),1400);return()=>clearTimeout(t);},[]);

  const navigateTo=(newPage)=>{
    setPage(newPage);
    setBloom(true);
    setTimeout(()=>setBloom(false),1400);
  };

  // ── Load all data after login ──────────────────────────────────────────────
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [rawUsers, rawProjects] = await Promise.all([
        api.get("/users"),
        api.get("/projects"),
      ]);
      const mappedUsers    = (rawUsers    || []).map(mapUser);
      const mappedProjects = (rawProjects || []).map(mapProject);
      setUsers(mappedUsers);
      setProjects(mappedProjects);
      // Load each project detail to get tasks with predecessor_ids
      const projectDetails = await Promise.all(
        mappedProjects.map(p=>api.get(`/projects/${p.id}`).catch(()=>null))
      );
      const allTasks = [];
      projectDetails.forEach(detail=>{
        if(!detail||!detail.tasks) return;
        detail.tasks.forEach(t=>{
          const mapped = mapTask(t);
          if(mapped) allTasks.push({...mapped, predecessorIds: t.predecessor_ids||[]});
        });
      });
      setTasks(allTasks);
      setDeps(buildDepsFromTasks(allTasks));
    } catch(e){
      setApiErr("Failed to load data: "+e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const doLogin = async () => {
    const email    = emailRef.current?.value.trim();
    const password = passRef.current?.value;
    if(!email||!password){ setLoginErr("Please enter email and password."); return; }
    setLoginErr("");
    try {
      const data = await api.post("/auth/login", { email, password });
      if(!data){ setLoginErr("Invalid email or password."); return; }
      api.setToken(data.access_token);
      const u = mapUser(data.user);
      setUser(u);
      await loadAllData();
    } catch(e){
      setLoginErr(e.message||"Login failed.");
    }
  };

  const doLogout = async () => {
    try { await api.post("/auth/logout",{}); } catch(_){}
    api.clearToken();
    setUser(null);
    setUsers([]); setProjects([]); setTasks([]); setDeps([]);
    navigateTo("dashboard");
  };

  const implied = useMemo(()=>computeImplied(tasks,deps),[tasks,deps]);

  if(!user) return <Login emailRef={emailRef} passRef={passRef} doLogin={doLogin} err={loginErr}/>;
  if(loading) return(
    <><style>{FONT+CSS}</style>
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"var(--bg)",color:"var(--pink)",fontFamily:"var(--font-ui)",fontSize:14,letterSpacing:".1em"}}>
      LOADING…
    </div></>
  );

  // ── Derived helpers ────────────────────────────────────────────────────────
  const projTasks   = pid => tasks.filter(t=>t.projectId===pid);
  const projDeps    = pid => { const ids=new Set(projTasks(pid).map(t=>t.id)); return deps.filter(d=>ids.has(d.predecessorId)&&ids.has(d.successorId)); };
  const getProgress = pid => { const ms=projTasks(pid); return ms.length?Math.round(ms.filter(t=>t.status==="complete").length/ms.length*100):0; };

  // ── Mutations ──────────────────────────────────────────────────────────────
  const addProject = async (d) => {
    try {
      const raw = await api.post("/projects", { name:d.name, description:d.description, colour:d.color||"#ff6eb4" });
      setProjects(ps=>[...ps, mapProject(raw)]);
    } catch(e){ alert("Could not create project: "+e.message); }
  };

  const addTask = async (d) => {
    try {
      const raw = await api.post("/tasks", {
        project_id:   d.projectId,
        name:         d.name,
        description:  d.notes||"",
        assignee_id:  d.assigneeId||null,
        planned_start:  dateToTs(d.startDate),
        planned_finish: dateToTs(d.finishDate),
        status:       toApiStatus(d.status||"not-started"),
        is_repeatable:  d.repeatable?1:0,
        repeat_interval: d.frequency||null,
      });
      setTasks(ts=>[...ts, mapTask(raw)]);
    } catch(e){ alert("Could not create task: "+e.message); }
  };

  const updateTask = async (id, data) => {
    try {
      const body = {};
      if(data.name        !== undefined) body.name            = data.name;
      if(data.notes       !== undefined) body.description     = data.notes;
      if(data.startDate   !== undefined) body.planned_start   = dateToTs(data.startDate);
      if(data.finishDate  !== undefined) body.planned_finish  = dateToTs(data.finishDate);
      if(data.assigneeId  !== undefined) body.assignee_id     = data.assigneeId;
      if(data.status      !== undefined) body.status          = toApiStatus(data.status);
      if(data.repeatable  !== undefined) body.is_repeatable   = data.repeatable?1:0;
      if(data.frequency   !== undefined) body.repeat_interval = data.frequency;
      const raw = await api.patch(`/tasks/${id}`, body);
      const mapped = mapTask(raw);
      if(mapped) setTasks(ts=>ts.map(t=>t.id===id ? { ...t, ...mapped } : t));
    } catch(e){ alert("Could not update task: "+e.message); }
  };

  const setTaskStatus = async (id, status) => {
    await updateTask(id, { status });
  };

  const deleteProj = async (id) => {
    try {
      await api.del(`/projects/${id}`);
      setProjects(ps=>ps.filter(p=>p.id!==id));
      setTasks(ts=>ts.filter(t=>t.projectId!==id));
      setDeps(ds=>{ const ids=new Set(tasks.filter(t=>t.projectId===id).map(t=>t.id)); return ds.filter(d=>!ids.has(d.predecessorId)&&!ids.has(d.successorId)); });
      if(selProj===id) setSelProj(null);
    } catch(e){ alert("Could not delete project: "+e.message); }
  };

  // Reload a single project's tasks+deps from the detail endpoint (which includes predecessor_ids)
  const reloadProject = async (projectId) => {
    try {
      const raw = await api.get(`/projects/${projectId}`);
      if(!raw||!raw.tasks) return;
      const freshTasks = raw.tasks.map(t=>({
        ...mapTask(t),
        predecessorIds: t.predecessor_ids||[],
      })).filter(Boolean);
      setTasks(ts=>{
        const others = ts.filter(t=>t.projectId!==projectId);
        return [...others, ...freshTasks];
      });
      setDeps(ds=>{
        const others = ds.filter(d=>!freshTasks.find(t=>t.id===d.successorId||t.id===d.predecessorId));
        const newDeps = buildDepsFromTasks(freshTasks);
        return [...others, ...newDeps];
      });
    } catch(e){ console.error("reloadProject failed",e); }
  };
  const addDep = async (d) => {
    const task = tasks.find(t=>t.id===d.successorId);
    if(!task) return;
    const newPredIds = [...new Set([...(task.predecessorIds||[]), d.predecessorId])];
    try {
      await api.patch(`/tasks/${d.successorId}`, { predecessor_ids: newPredIds });
      await reloadProject(task.projectId);
    } catch(e){ alert("Could not save dependency: "+e.message); }
  };

  const removeDep = async (depId) => {
    const dep = deps.find(d=>d.id===depId);
    if(!dep) return;
    const task = tasks.find(t=>t.id===dep.successorId);
    if(!task) return;
    const newPredIds = (task.predecessorIds||[]).filter(pid=>pid!==dep.predecessorId);
    try {
      await api.patch(`/tasks/${dep.successorId}`, { predecessor_ids: newPredIds });
      await reloadProject(task.projectId);
    } catch(e){ alert("Could not remove dependency: "+e.message); }
  };

  const updateLag = (id, lag) => {
    // Lag is UI-only for now (backend stores no lag); update locally
    setDeps(ds=>ds.map(d=>d.id===id?{...d,lagDays:Number(lag)}:d));
  };

  const updateUserColor = async (id, color) => {
    try {
      await api.patch(`/users/${id}`, { avatar_colour: color });
      setUsers(us=>us.map(u=>u.id===id?{...u,color}:u));
      if(user.id===id) setUser(u=>({...u,color}));
    } catch(e){ alert("Could not update colour: "+e.message); }
  };

  const addComment = async (taskId, comment) => {
    try {
      const raw = await api.post(`/api/tasks/${taskId}/comments`, { body: comment.text });
      const mapped = mapComment(raw);
      setTasks(ts=>ts.map(t=>t.id===taskId?{...t,comments:[...(t.comments||[]),mapped]}:t));
    } catch(e){ alert("Could not post comment: "+e.message); }
  };

  // Load comments when edit modal opens
  const loadComments = async (taskId) => {
    try {
      const raw = await api.get(`/api/tasks/${taskId}/comments`);
      const mapped = (raw||[]).map(mapComment);
      setTasks(ts=>ts.map(t=>t.id===taskId?{...t,comments:mapped}:t));
    } catch(_){}
  };

  const myOpenTasks   = tasks.filter(t=>t.status!=="complete"&&t.assigneeId===user.id);
  const totalOverdue  = tasks.filter(t=>{ const i=implied[t.id]; return t.status!=="complete"&&i&&i.impliedFinish<new Date(); }).length;

  return(
    <>
      <style>{FONT+CSS}</style>
      <div className="shell">
        <TopBar user={user} logout={doLogout}/>
        <div className="main">
          <Sidebar page={page} projects={projects} selProj={selProj}
            onNav={navigateTo} onSelProj={id=>{setSelProj(id);navigateTo('gantt');}}/>
          {bloom&&<div className="phosphor-bloom"/>}
          {apiErr&&<div style={{position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",background:"#ff224488",color:"#fff",padding:"10px 20px",borderRadius:8,zIndex:9999,fontFamily:"var(--font-body)",fontSize:13}}>{apiErr}</div>}
          <div key={page} className="content phosphor-item-3">
            {page==="dashboard"&&<Dashboard user={user} projects={projects} tasks={tasks} implied={implied}
              myOpenTasks={myOpenTasks} totalOverdue={totalOverdue} getProgress={getProgress}
              onOpenProj={id=>{setSelProj(id);navigateTo('projects');}} onNewProj={()=>setModal({type:"project"})}
              users={users} setTaskStatus={setTaskStatus} updateTask={updateTask}
              onEditTask={t=>{ loadComments(t.id); setModal({type:'editTask',taskId:t.id}); }}/>}
            {page==="projects"&&<ProjectsPage projects={projects} tasks={tasks} implied={implied} deps={deps}
              selProj={selProj} setSelProj={setSelProj} users={users} user={user}
              getProgress={getProgress} setTaskStatus={setTaskStatus} deleteProj={deleteProj}
              updateTask={updateTask}
              onNewTask={pid=>setModal({type:'task',projectId:pid})}
              onEditTask={t=>{ loadComments(t.id); setModal({type:'editTask',taskId:t.id}); }}
              onManageDeps={t=>setModal({type:'deps',taskId:t.id})}
              onNewProj={()=>setModal({type:"project"})}/>}
            {page==="gantt"&&<GanttPage projects={projects} tasks={tasks} deps={deps} implied={implied}
              selProj={selProj} setSelProj={setSelProj} users={users}
              projTasks={projTasks} projDeps={projDeps}/>}
            {page==="team"&&<TeamPage users={users} tasks={tasks} implied={implied} user={user} updateUserColor={updateUserColor}/>}
          </div>
        </div>
        <MobileNav page={page} onNav={navigateTo}/>
        {modal&&<ModalRouter modal={modal} setModal={setModal} projects={projects} tasks={tasks}
          deps={deps} users={users} user={user} addProject={addProject} addTask={addTask}
          updateTask={updateTask} addDep={addDep} removeDep={removeDep} updateLag={updateLag} onAddComment={addComment}/>}
      </div>
    </>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login({emailRef,passRef,doLogin,err}){
  const [bloom,setBloom]=useState(true);
  useEffect(()=>{const t=setTimeout(()=>setBloom(false),1600);return()=>clearTimeout(t);},[]);
  return(
    <><style>{FONT+CSS}</style>
    {bloom&&<div className="phosphor-bloom"/>}
    <div className="login-wrap">
      {/* Sky layers */}
      <div className="outrun-sky phosphor-item-1"/>
      <div className="outrun-sky-glow phosphor-item-1"/>
      {/* Sun */}
      <div className="outrun-sun phosphor-item-2">
        <div className="outrun-sun-stripes"/>
      </div>
      {/* Horizon */}
      <div className="outrun-horizon phosphor-item-2"/>
      {/* Floor grid */}
      <div className="outrun-floor phosphor-item-3">
        <OutrunGridSVG width={1200} height={500} numV={24} numH={14}/>
      </div>
      {/* Logo floating in sky */}
      <div className="outrun-logo phosphor-item-2">
        <div className="outrun-kascomm">KASCOMM</div>
        <div className="outrun-rule"/>
        <div className="outrun-hub">· H U B ·</div>
        <div className="outrun-tagline">Family Project Command</div>
      </div>
      {/* Frosted login card */}
      <div className="login-card-wrap phosphor-item-4">
        <div className="login-card">
          <h1>Welcome Back</h1>
          <div className="sub">Sign in to your family workspace</div>
          <div className="field"><label>Email</label><input ref={emailRef} type="email" placeholder="you@family.home" onKeyDown={e=>e.key==='Enter'&&doLogin()}/></div>
          <div className="field"><label>Password</label><input ref={passRef} type="password" placeholder="••••" onKeyDown={e=>e.key==='Enter'&&doLogin()}/></div>
          {err&&<div className="err">{err}</div>}
          <button className="btn btn-primary" style={{width:"100%",marginTop:8}} onClick={doLogin}>Sign In</button>
        </div>
      </div>
    </div></>
  );
}

// ─── OUTRUN GRID SVG ─────────────────────────────────────────────────────────
function OutrunGridSVG({width,height,numV=14,numH=14,color="#b06eff"}){
  const lines=[];
  const cx=width/2;
  // Vertical lines — parallel fan: 1 unit apart at top, 3.5 units apart at bottom
  // Centre line first, then pairs outward until top exits frame
  const topUnit=width/18;
  const botUnit=topUnit*3.5;
  let vi=0;
  while(true){
    const topX=cx+vi*topUnit;
    if(vi>0&&topX>width)break;
    const draw=(tx,bx)=>lines.push(
      <line key={"v"+vi+(tx<cx?"L":"R")} x1={tx} y1={0} x2={bx} y2={height}
        stroke={color} strokeWidth={.7} strokeOpacity={.4}/>
    );
    if(vi===0){draw(cx,cx);}
    else{draw(cx+vi*topUnit,cx+vi*botUnit);draw(cx-vi*topUnit,cx-vi*botUnit);}
    vi++;
    if(vi>200)break;
  }
  // Horizontal lines — tight at top (far/horizon), spread at bottom (near/camera)
  const focal=0.6;
  for(let i=1;i<=numH;i++){
    const worldZ=(numH-i+1)/numH;
    const perspFrac=1-(worldZ/(worldZ+focal*(1-worldZ)));
    const y=perspFrac*height;
    lines.push(<line key={"h"+i} x1={0} y1={y} x2={width} y2={y}
      stroke={color} strokeWidth={.7} strokeOpacity={.15+(1-perspFrac)*.5}/>);
  }
  return(
    <svg className="outrun-grid-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {lines}
    </svg>
  );
}

// ─── LOGO INLINE (topbar) ─────────────────────────────────────────────────────
function LogoInline(){
  return(
    <div className="logo-inline">
      <span className="logo-inline-kascomm">KASCOMM</span>
      <span className="logo-inline-sep">·</span>
      <span className="logo-inline-hub">HUB</span>
    </div>
  );
}

// ─── PIXEL CAR ANIMATION ─────────────────────────────────────────────────────
function PixelCarCanvas({width,height}){
  const canvasRef=useRef();
  useEffect(()=>{
    const canvas=canvasRef.current;
    const ctx=canvas.getContext('2d');
    let raf;
    const P=3;  // pixel size — increased for bigger car
    const state={
      frame:0,roadOffset:0,wheelFrame:0,
      exhaustParticles:[],
      stars:Array.from({length:18},()=>({
        x:Math.random()*width,y:Math.random()*(height*0.5),
        speed:.3+Math.random()*.8,size:Math.random()<.3?2:1,
        twinkle:Math.random()*Math.PI*2
      })),
      buildings:[
        {x:10,w:4,h:4},{x:18,w:3,h:5},{x:25,w:5,h:3},{x:34,w:3,h:5},
        {x:41,w:4,h:3},{x:49,w:3,h:6},{x:56,w:5,h:4},{x:65,w:3,h:5},
        {x:72,w:4,h:3},{x:80,w:3,h:6},{x:87,w:5,h:3},{x:96,w:3,h:5},
        {x:103,w:4,h:4},{x:111,w:3,h:5},{x:118,w:5,h:3},{x:127,w:3,h:4},
        {x:134,w:4,h:5},{x:142,w:3,h:4},{x:149,w:5,h:6},{x:157,w:3,h:4},
      ]
    };
    function drawRect(x,y,w,h,c){ctx.fillStyle=c;ctx.fillRect(Math.round(x)*P,Math.round(y)*P,w*P,h*P);}
    function drawCar(ox,oy){
      // Bigger car sprite — 20×9 pixels
      const wc=state.wheelFrame<2?'#b06eff':'#8844dd';
      const wc2=state.wheelFrame<2?'#cc88ff':'#aa66ff';
      // Shadow
      ctx.fillStyle='rgba(176,110,255,.18)';
      ctx.fillRect((ox+1)*P,(oy+9)*P,18*P,P);
      // Body lower
      drawRect(ox,  oy+5,20,3,'#ff6eb4');
      // Body upper / cabin
      drawRect(ox+3,oy+2,13,4,'#ff9fd4');
      // Windscreen front + rear
      drawRect(ox+4, oy+3,4,2,'#40e0d0');
      drawRect(ox+10,oy+3,4,2,'#40e0d0');
      // Roof detail
      drawRect(ox+5, oy+2,8,1,'#ffccee');
      // Headlights
      drawRect(ox+18,oy+5,1,1,'#ffffaa');
      drawRect(ox+19,oy+4,1,3,'#ffff44');
      // Tail lights
      drawRect(ox,   oy+5,1,1,'#ff2244');
      drawRect(ox,   oy+6,1,1,'#ff4466');
      // Door line
      drawRect(ox+8, oy+5,1,3,'#cc3380');
      // Wheels (front + rear)
      drawRect(ox+2, oy+7,4,3,'#111122');
      drawRect(ox+14,oy+7,4,3,'#111122');
      drawRect(ox+3, oy+8,2,1,wc2);
      drawRect(ox+15,oy+8,2,1,wc2);
      drawRect(ox+4, oy+7,1,1,wc);
      drawRect(ox+16,oy+7,1,1,wc);
      // Exhaust
      if(state.wheelFrame===0){
        state.exhaustParticles.push({x:ox-2,y:oy+5,life:10});
        state.exhaustParticles.push({x:ox-1,y:oy+6,life:7});
      }
    }
    function tick(){
      state.frame++;
      state.roadOffset+=1.5;
      state.wheelFrame=Math.floor(state.frame/4)%4;
      ctx.clearRect(0,0,canvas.width,canvas.height);
      const roadY=height*.42;  // horizon higher — more road visible below
      // Sky
      const sg=ctx.createLinearGradient(0,0,0,roadY*P);
      sg.addColorStop(0,'#080018');sg.addColorStop(1,'#1a0040');
      ctx.fillStyle=sg;ctx.fillRect(0,0,width*P,roadY*P);
      // Stars
      state.stars.forEach(s=>{
        s.x-=s.speed;if(s.x<0)s.x=width+2;
        const tw=.4+Math.sin(s.twinkle+state.frame*.05)*.25;
        ctx.fillStyle=`rgba(255,200,255,${tw})`;
        ctx.fillRect(Math.round(s.x)*P,Math.round(s.y)*P,s.size*P,s.size*P);
      });
      // Buildings
      state.buildings.forEach(b=>{
        const bx=((b.x-state.roadOffset*.15)%(width+30)+width+30)%(width+30)-15;
        ctx.fillStyle='#1a0035';ctx.fillRect(bx*P,(roadY-b.h)*P,b.w*P,b.h*P);
        if(b.h>7){
          ctx.fillStyle=state.frame%60<30?'rgba(176,110,255,.8)':'rgba(64,224,208,.6)';
          ctx.fillRect((bx+1)*P,(roadY-b.h+1)*P,P,P);
          ctx.fillRect((bx+3)*P,(roadY-b.h+3)*P,P,P);
        }
      });
      // Road
      const rg=ctx.createLinearGradient(0,roadY*P,0,height*P);
      rg.addColorStop(0,'#0a0a1a');rg.addColorStop(1,'#050510');
      ctx.fillStyle=rg;ctx.fillRect(0,roadY*P,width*P,(height-roadY)*P);
      // Horizon glow
      ctx.fillStyle='rgba(176,110,255,.35)';ctx.fillRect(0,(roadY-1)*P,width*P,2*P);
      ctx.fillStyle='rgba(255,110,180,.2)';ctx.fillRect(0,(roadY-.5)*P,width*P,P);
      // Road edge
      ctx.fillStyle='rgba(64,224,208,.6)';ctx.fillRect(0,(roadY+1)*P,width*P,P);
      // Dashes
      const dW=7,gW=5,tot=dW+gW,sx=-((state.roadOffset%tot));
      const midY=roadY+(height-roadY)*.3;
      for(let x=sx;x<width;x+=tot){
        ctx.fillStyle='rgba(255,110,180,.45)';ctx.fillRect(x*P,midY*P,dW*P,P);
      }
      // Exhaust
      state.exhaustParticles=state.exhaustParticles.filter(p=>{
        ctx.fillStyle=`rgba(176,110,255,${p.life/8*.5})`;
        ctx.fillRect(p.x*P,p.y*P,P,P);
        p.x-=.5;p.y-=.15;p.life--;return p.life>0;
      });
      // Car
      // Car sits in lower half of floor zone, fully visible
      const carY=Math.round(roadY+(height-roadY)*.35);
      drawCar(Math.round(width*.15),carY);
      raf=requestAnimationFrame(tick);
    }
    raf=requestAnimationFrame(tick);
    return()=>cancelAnimationFrame(raf);
  },[width,height]);
  return <canvas ref={canvasRef} width={width*2} height={height*2} style={{width,height,display:'block'}}/>;
}

// ─── TOPBAR ───────────────────────────────────────────────────────────────────
function TopBar({user,logout}){
  return(
    <div className="topbar phosphor-item-1">
      <LogoInline/>
      <div className="topbar-sep"/>
      <span className="topbar-sub">Project Tracker</span>
      <div className="spacer"/>
      <div style={{borderRadius:8,overflow:'hidden',border:'1px solid rgba(176,110,255,.3)',flexShrink:0}}>
        <PixelCarCanvas width={220} height={38}/>
      </div>
      <div className="av" style={{width:28,height:28,background:user.color+"28",color:user.color,fontSize:10,cursor:"pointer",flexShrink:0}} onClick={logout} title="Sign out">{user.initials}</div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({page,projects,selProj,onNav,onSelProj}){
  return(
    <div className="sidebar phosphor-item-2">
      {/* Miniature Outrun header */}
      <div className="sidebar-outrun">
        <div className="sb-sky"/>
        <div className="sb-sky-glow"/>
        <div className="sb-sun"><div className="sb-sun-stripes"/></div>
        <div className="sb-horizon"/>
        <div className="sb-floor">
          <OutrunGridSVG width={220} height={80} numV={14} numH={8}/>
        </div>
        <div className="sb-logo">
          <div className="sb-kascomm">KASCOMM</div>
          <div className="sb-rule"/>
          <div className="sb-hub">· H U B ·</div>
        </div>
      </div>
      <div className="nav-section">
        <div className="nav-label">Main</div>
        {[["dashboard","◈","Dashboard"],["projects","▦","Projects"],["gantt","▬","Gantt"],["team","◉","Team"]].map(([id,icon,label])=>(
          <button key={id} className={`nav-item ${page===id?"active":""}`} onClick={()=>onNav(id)}>
            <span className="nav-icon">{icon}</span>{label}
          </button>
        ))}
      </div>
      <div className="nav-section" style={{marginTop:8}}>
        <div className="nav-label">Projects</div>
        {projects.map(p=>(
          <button key={p.id} className={`nav-item ${page==="gantt"&&selProj===p.id?"active":""}`} onClick={()=>onSelProj(p.id)}>
            <div className="dot" style={{background:p.color}}/>
            <span className="content-title" style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:13}}>{p.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MOBILE NAV ───────────────────────────────────────────────────────────────
function MobileNav({page,onNav}){
  return(
    <div className="mobile-nav">
      {[["dashboard","◈","Home"],["projects","▦","Projects"],["team","◉","Team"]].map(([id,icon,label])=>(
        <button key={id} className={`mob-nav-item ${page===id?"active":""}`} onClick={()=>onNav(id)}>
          <span className="mob-icon">{icon}</span>{label}
        </button>
      ))}
    </div>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
function EmptyState({message,sub}){
  const W=600,H=160,color="#b06eff";
  const lines=[];
  const cx=W/2;
  const topUnit=W/18;
  const botUnit=topUnit*3.5;
  let vi=0;
  while(true){
    const topX=cx+vi*topUnit;
    if(vi>0&&topX>W)break;
    const draw=(tx,bx)=>lines.push(
      <line key={"v"+vi+(tx<cx?"L":"R")} x1={tx} y1={0} x2={bx} y2={H}
        stroke={color} strokeWidth={.6} strokeOpacity={.35}/>
    );
    if(vi===0){draw(cx,cx);}
    else{draw(cx+vi*topUnit,cx+vi*botUnit);draw(cx-vi*topUnit,cx-vi*botUnit);}
    vi++;
    if(vi>200)break;
  }
  const numH=10,focal=0.6;
  for(let i=1;i<=numH;i++){
    const worldZ=(numH-i+1)/numH;
    const perspFrac=1-(worldZ/(worldZ+focal*(1-worldZ)));
    const y=perspFrac*H;
    lines.push(<line key={"h"+i} x1={0} y1={y} x2={W} y2={y}
      stroke={color} strokeWidth={.6} strokeOpacity={.1+(1-perspFrac)*.45}/>);
  }
  return(
    <div className="empty-state">
      <div className="empty-sky-glow"/>
      <div className="empty-message">{message}</div>
      {sub&&<div className="empty-sub">{sub}</div>}
      <div className="empty-horizon"/>
      <div className="empty-grid">
        <svg className="empty-grid-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          {lines}
        </svg>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
// ─── SHARED DATE CELL ─────────────────────────────────────────────────────────
// Parses yyyy-mm-dd without timezone shift
function parseDateLocal(str){ if(!str)return null; const[y,m,d]=str.split("-").map(Number); return new Date(y,m-1,d); }

function DateCell({value, isImplied, color, fontWeight, onChange, compareDate, displayValue}){
  const inputRef=useRef();
  const [localVal,setLocalVal]=useState(value||"");
  useEffect(()=>{ setLocalVal(value||""); },[value]);
  // For implied dates, displayValue is pre-formatted — use it directly to avoid Safari parsing issues
  // For planned dates, value is yyyy-mm-dd — format it via fmtDisplay
  const display=displayValue||(value?fmtDisplay(value):"—");

  if(isImplied){
    return(
      <div style={{width:70,textAlign:"center",fontFamily:"var(--font-display)",fontSize:14,
        color:color||"var(--muted2)",fontWeight:fontWeight||400,
        background:"rgba(64,224,208,.06)",borderRadius:4,padding:"2px 0",whiteSpace:"nowrap",
        cursor:"default"}}>
        {display}
      </div>
    );
  }
  return(
    <div style={{position:"relative",width:70}}>
      <input type="date" ref={inputRef}
        value={localVal}
        onChange={e=>{
          let v=e.target.value;
          if(!v) return;
          // Finish-before-start guard
          if(compareDate && parseDateLocal(v) < parseDateLocal(compareDate)) v=compareDate;
          setLocalVal(v);
          if(v!==value) onChange(v);
        }}
        style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",
          cursor:"pointer",zIndex:1,pointerEvents:"none"}}/>
      <div
        title="Click to change date"
        onClick={()=>{ inputRef.current&&inputRef.current.showPicker&&inputRef.current.showPicker(); }}
        style={{width:70,textAlign:"center",fontFamily:"var(--font-display)",fontSize:14,
          color:color||"var(--muted2)",fontWeight:fontWeight||400,
          borderRadius:4,padding:"2px 0",whiteSpace:"nowrap",
          cursor:"pointer",transition:"background .15s"}}
        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,110,180,.1)"}
        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
        {display}
      </div>
    </div>
  );
}

function Dashboard({user,projects,tasks,implied,myOpenTasks,totalOverdue,getProgress,onOpenProj,onNewProj,users,setTaskStatus,onEditTask,updateTask}){
  const [drill,setDrill]=useState(null);
  const leftColors=["","teal-left","purple-left"];
  const now=new Date();
  const todayStr=now.toISOString().slice(0,10);
  const twoWeeksAgo=new Date(now);twoWeeksAgo.setDate(twoWeeksAgo.getDate()-14);
  const todayCount=tasks.filter(t=>t.finishDate===todayStr).length;
  const recentDone=tasks.filter(t=>t.status==="complete"&&t.completedAt&&new Date(t.completedAt)>=twoWeeksAgo).length;
  if(drill){
    const titles={today:"Today's Items",myopen:"My Open Tasks",overdue:"Implied Overdue",complete:"Completed — Last 14 Days"};
    const drillTasks={
      today:tasks.filter(t=>t.finishDate===todayStr),
      myopen:myOpenTasks,
      overdue:tasks.filter(t=>{const i=implied[t.id];return t.status!=="complete"&&i&&i.impliedFinish<now;}),
      complete:tasks.filter(t=>t.status==="complete"&&t.completedAt&&new Date(t.completedAt)>=twoWeeksAgo)
    }[drill]||[];
    return(
      <div className="phosphor-item-3">
        <div className="page-hdr">
          <button className="btn btn-ghost btn-sm" onClick={()=>setDrill(null)}>&#8592; Home</button>
          <h2>{titles[drill]}</h2>
          <span style={{background:"var(--surface2)",border:"1px solid var(--border2)",borderRadius:20,padding:"2px 10px",fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginLeft:4}}>{drillTasks.length}</span>
        </div>
        {drillTasks.length===0&&<EmptyState message="Nothing here right now." sub="Check back later"/>}
        {drillTasks.length>0&&(()=>{
          const COL="500px 32px 70px 70px 70px 70px 100px 44px";
          const cellStyle={display:"flex",alignItems:"center",justifyContent:"center"};
          return(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {/* Header */}
              <div className="task-row-desktop" style={{display:"grid",gap:"0 8px",padding:"4px 12px",
                fontFamily:"var(--font-ui)",fontSize:10,fontWeight:700,letterSpacing:".08em",
                textTransform:"uppercase",color:"var(--muted)",borderBottom:"1px solid var(--border2)"}}>
                <span>Task</span><span></span>
                <span className="task-col-dates" style={{textAlign:"center"}}>Start</span>
                <span className="task-col-dates" style={{textAlign:"center"}}>Finish</span>
                <span className="task-col-dates" style={{textAlign:"center",color:"var(--teal)",opacity:.7}}>Impl. Start</span>
                <span style={{textAlign:"center",color:"var(--teal)",opacity:.7}}>Impl. Finish</span>
                <span style={{textAlign:"center"}}>Status</span>
                <span></span>
              </div>
              {drillTasks.map((t)=>{
                const proj=projects.find(p=>p.id===t.projectId);
                const assignee=users.find(u=>u.id===t.assigneeId);
                const impl=implied[t.id];
                const shifted=impl&&fmtDate(impl.impliedStart)!==t.startDate;
                const overdue=impl&&t.status!=="complete"&&impl.impliedFinish<now;
                const st=t.status||"not-started";
                return(
                  <div key={t.id} className="phosphor-card task-row-desktop" style={{display:"grid",
                    gap:"0 8px",padding:"10px 12px",
                    background:t.status==="complete"?"rgba(119,102,170,.06)":"var(--surface)",
                    border:"1px solid var(--border)",borderLeft:"3px solid "+(proj?proj.color:"var(--pink)"),
                    borderRadius:10,alignItems:"center",opacity:t.status==="complete"?0.55:1}}>
                    {/* Task name + project subtitle */}
                    <div style={{overflow:"hidden",cursor:"pointer"}} onClick={()=>onEditTask(t)}>
                      <div className="content-title task-name-link" style={{fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                      {proj&&<div style={{fontFamily:"var(--font-body)",fontSize:10,color:"var(--muted)",marginTop:2,display:"flex",alignItems:"center",gap:4}}>
                        <div style={{width:5,height:5,borderRadius:"50%",background:proj.color,flexShrink:0}}/>{proj.name}
                      </div>}
                    </div>
                    {/* Avatar */}
                    <div style={cellStyle}>
                      {assignee&&<div className="av" style={{width:24,height:24,background:assignee.color+"28",color:assignee.color,fontSize:10,borderRadius:"50%"}}>{assignee.initials}</div>}
                    </div>
                    {/* Planned start */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell value={t.startDate} isImplied={false}
                        onChange={val=>updateTask(t.id,{startDate:val})}/>
                    </div>
                    {/* Planned finish */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell value={t.finishDate} isImplied={false}
                        compareDate={t.startDate}
                        onChange={val=>updateTask(t.id,{finishDate:val})}/>
                    </div>
                    {/* Implied start */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell isImplied={true}
                        displayValue={impl?fmtDisplay(impl.impliedStart):"—"}
                        color={shifted?"var(--warning)":"var(--muted2)"}/>
                    </div>
                    {/* Implied finish */}
                    <div style={cellStyle}>
                      <DateCell isImplied={true}
                        displayValue={impl?fmtDisplay(impl.impliedFinish):t.finishDate?fmtDisplay(t.finishDate):"—"}
                        color={overdue?"var(--danger)":shifted?"var(--warning)":"var(--muted2)"}
                        fontWeight={overdue?700:400}/>
                    </div>
                    {/* Status */}
                    <div style={cellStyle}>
                      <select value={st} onChange={e=>setTaskStatus(t.id,e.target.value)}
                        style={{background:taskStatusBg[st],color:taskStatusColor[st],
                          border:"1px solid currentColor",borderRadius:20,padding:"4px 6px",
                          fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".06em",
                          textTransform:"uppercase",cursor:"pointer",outline:"none",
                          appearance:"none",WebkitAppearance:"none",width:"100%",textAlign:"center"}}>
                        {TASK_STATUSES.map(s=><option key={s} value={s}>{taskStatusLabel[s]}</option>)}
                      </select>
                    </div>
                    {/* Edit */}
                    <div style={cellStyle}>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:"4px 8px"}} onClick={()=>onEditTask(t)}>Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  }
  return(
    <div className="fade-up">
      <div className="page-hdr">
        <div>
          <h2>Hello, {user.name} 👋</h2>
          <p style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",marginTop:3}}>Your family project snapshot</p>
        </div>
        <div className="spacer"/>
        <button className="btn btn-primary" onClick={onNewProj}>+ New Project</button>
      </div>
      <div className="stat-grid">
        {[
          [todayCount,    "Today's Items",   "var(--pink)",   "today"],
          [recentDone,    "Tasks Complete",  "var(--teal)",   "complete"],
          [myOpenTasks.length,"My Open Tasks","var(--accent2)","myopen"],
          [totalOverdue,  "Implied Overdue", "var(--danger)", "overdue"],
        ].map(([val,lbl,col,drill],i)=>(
          <div key={i} className="stat-card" style={{cursor:"pointer"}}
            onClick={()=>setDrill(drill)}>
            <div className="stat-val" style={{color:col}}>{val}</div>
            <div className="stat-lbl">{lbl}</div>
          </div>
        ))}
      </div>
      {projects.length===0&&<EmptyState message="The grid is clear. Time to build something." sub="Tap + New Project to begin"/>}
      {projects.map((p,i)=>{
        const prog=getProgress(p.id);
        const pt=tasks.filter(t=>t.projectId===p.id);
        return(
          <div key={p.id} className={`card ${leftColors[i%3]} phosphor-card`} style={{cursor:"pointer"}} onClick={()=>onOpenProj(p.id)}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:9,height:9,borderRadius:"50%",background:p.color,flexShrink:0,boxShadow:"0 0 6px "+p.color}}/>
              <div className="content-title" style={{fontSize:20,flex:1,color:"var(--text)"}}>{p.name}</div>
              <div className={`badge ${projStatusClass[p.status]||"badge-not-started"}`}>{projStatusLabel[p.status]||p.status}</div>
            </div>
            <div style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",marginBottom:12}}>{p.description}</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div className="prog-bar" style={{flex:1}}><div className="prog-fill prog-fill-gradient" style={{width:prog+"%"}}/></div>
              <span style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:700,color:"var(--muted2)",minWidth:36,textAlign:"right"}}>{prog}%</span>
            </div>
            <div style={{display:"flex",gap:16,marginTop:8}}>
              <span style={{fontFamily:"var(--font-body)",fontSize:12,color:"var(--muted)"}}>🏁 {pt.length} tasks</span>
              <span style={{fontFamily:"var(--font-body)",fontSize:12,color:"var(--muted)"}}>✅ {pt.filter(t=>t.status==="complete").length} done</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── PROJECTS PAGE ────────────────────────────────────────────────────────────
function ProjectsPage({projects,tasks,implied,deps,selProj,setSelProj,users,user,getProgress,setTaskStatus,deleteProj,onNewTask,onEditTask,onManageDeps,onNewProj,updateTask}){
  const proj=selProj?projects.find(p=>p.id===selProj):null;
  if(proj){
    const pt=tasks.filter(t=>t.projectId===proj.id);
    const prog=getProgress(proj.id);
    return(
      <div className="fade-up">
        <div className="page-hdr">
          <button className="btn btn-ghost btn-sm" onClick={()=>setSelProj(null)}>← All</button>
          <div style={{width:9,height:9,borderRadius:"50%",background:proj.color,boxShadow:"0 0 8px "+proj.color}}/>
          <h2 className="content-title" style={{fontSize:22,fontFamily:"var(--font-content)"}}>{proj.name}</h2>
          <div className={`badge ${projStatusClass[proj.status]||"badge-not-started"}`}>{projStatusLabel[proj.status]||proj.status}</div>
          <div className="spacer"/>
          <button className="btn btn-primary btn-sm" onClick={()=>onNewTask(proj.id)}>+ Task</button>
          <button className="btn btn-danger btn-sm" onClick={()=>deleteProj(proj.id)}>Delete</button>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <div className="prog-bar" style={{flex:1,height:7}}><div className="prog-fill prog-fill-gradient" style={{width:prog+"%"}}/></div>
          <span style={{fontFamily:"var(--font-ui)",fontSize:14,fontWeight:700}}>{prog}%</span>
        </div>
        {/* Task list — compact on mobile, full table on desktop */}
        {pt.length===0&&<EmptyState message="Nothing on the radar yet. Add a task to get moving." sub="Tap + Task to add your first task"/>}
        {pt.length>0&&(()=>{
          const COL="500px 32px 70px 70px 70px 70px 100px 44px";
          const cellStyle={display:"flex",alignItems:"center",justifyContent:"center"};

          return(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {/* Header row — same COL template as data rows so labels align exactly */}
              <div className="task-row-desktop" style={{display:"grid",gap:"0 8px",padding:"4px 12px",
                fontFamily:"var(--font-ui)",fontSize:10,fontWeight:700,letterSpacing:".08em",
                textTransform:"uppercase",color:"var(--muted)",borderBottom:"1px solid var(--border2)"}}>
                <span>Task</span>
                <span></span>
                <span className="task-col-dates" style={{textAlign:"center"}}>Start</span>
                <span className="task-col-dates" style={{textAlign:"center"}}>Finish</span>
                <span className="task-col-dates" style={{textAlign:"center",color:"var(--teal)",opacity:.7}}>Impl. Start</span>
                <span style={{textAlign:"center",color:"var(--teal)",opacity:.7}}>Impl. Finish</span>
                <span style={{textAlign:"center"}}>Status</span>
                <span></span>
              </div>
              {pt.map(t=>{
                const impl=implied[t.id];
                const assignee=users.find(u=>u.id===t.assigneeId);
                const startShifted=impl&&fmtDate(impl.impliedStart)!==t.startDate;
                const isOverdue=impl&&t.status!=="complete"&&impl.impliedFinish<new Date();
                const st=t.status||"not-started";
                return(
                  <div key={t.id} className="phosphor-card task-row-desktop" style={{display:"grid",
                    gap:"0 8px",padding:"10px 12px",
                    background:t.status==="complete"?"rgba(119,102,170,.06)":"var(--surface)",
                    border:"1px solid var(--border)",borderLeft:"3px solid "+(proj.color||"var(--pink)"),
                    borderRadius:10,alignItems:"center",opacity:t.status==="complete"?.55:1}}>

                    {/* Task name */}
                    <div style={{display:"flex",alignItems:"center",gap:6,overflow:"hidden",cursor:"pointer"}} onClick={()=>onEditTask(t)}>
                      <div className="content-title task-name-link" style={{fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                      {t.comments&&t.comments.length>0&&<div style={{flexShrink:0,background:"rgba(176,110,255,.2)",color:"var(--purple)",borderRadius:20,padding:"1px 6px",fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".06em"}}>{t.comments.length}</div>}
                      {t.repeatable&&<div title={"Repeats "+t.frequency} style={{flexShrink:0,background:"rgba(64,224,208,.15)",color:"var(--teal)",borderRadius:20,padding:"1px 6px",fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".06em"}}>↺</div>}
                    </div>

                    {/* Avatar */}
                    <div style={cellStyle}>
                      {assignee&&<div className="av" style={{width:24,height:24,background:assignee.color+"28",color:assignee.color,fontSize:10,borderRadius:"50%"}}>{assignee.initials}</div>}
                    </div>

                    {/* Planned start — calendar popup */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell value={t.startDate} isImplied={false}
                        onChange={val=>updateTask(t.id,{startDate:val})}/>
                    </div>

                    {/* Planned finish — calendar popup */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell value={t.finishDate} isImplied={false}
                        compareDate={t.startDate}
                        onChange={val=>updateTask(t.id,{finishDate:val})}/>
                    </div>

                    {/* Implied start — read only */}
                    <div className="task-col-dates" style={cellStyle}>
                      <DateCell isImplied={true}
                        displayValue={impl?fmtDisplay(impl.impliedStart):"—"}
                        color={startShifted?"var(--warning)":"var(--muted2)"}/>
                    </div>

                    {/* Implied finish — read only */}
                    <div style={cellStyle}>
                      <DateCell isImplied={true}
                        displayValue={impl?fmtDisplay(impl.impliedFinish):t.finishDate?fmtDisplay(t.finishDate):"—"}
                        color={isOverdue?"var(--danger)":startShifted?"var(--warning)":"var(--muted2)"}
                        fontWeight={isOverdue?700:400}/>
                    </div>

                    {/* Status */}
                    <div style={cellStyle}>
                      <select value={st} onChange={e=>setTaskStatus(t.id,e.target.value)}
                        style={{background:taskStatusBg[st],color:taskStatusColor[st],
                          border:"1px solid currentColor",borderRadius:20,padding:"4px 6px",
                          fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".06em",
                          textTransform:"uppercase",cursor:"pointer",outline:"none",
                          appearance:"none",WebkitAppearance:"none",width:"100%",textAlign:"center"}}>
                        {TASK_STATUSES.map(s=><option key={s} value={s}>{taskStatusLabel[s]}</option>)}
                      </select>
                    </div>

                    {/* Edit */}
                    <div style={cellStyle}>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:"4px 8px"}} onClick={()=>onEditTask(t)}>Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  }
  const leftColors=["","teal-left","purple-left"];
  return(
    <div className="fade-up">
      <div className="page-hdr"><h2>All Projects</h2><div className="spacer"/><button className="btn btn-primary" onClick={onNewProj}>+ New Project</button></div>
      {projects.length===0&&<EmptyState message="The grid is clear. Time to build something." sub="Create your first project to get started"/>}
      {projects.length===0&&<EmptyState message="The grid is clear. Time to build something." sub="Tap + New Project to begin"/>}
      {projects.map((p,i)=>{
        const prog=getProgress(p.id);
        const pt=tasks.filter(t=>t.projectId===p.id);
        return(
          <div key={p.id} className={`card ${leftColors[i%3]} phosphor-card`} style={{cursor:"pointer"}} onClick={()=>setSelProj(p.id)}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:9,height:9,borderRadius:"50%",background:p.color,boxShadow:"0 0 6px "+p.color}}/>
              <div className="content-title" style={{fontSize:20,flex:1}}>{p.name}</div>
              <div className={`badge ${projStatusClass[p.status]||"badge-not-started"}`}>{projStatusLabel[p.status]||p.status}</div>
            </div>
            <div style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",marginBottom:12}}>{p.description}</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div className="prog-bar" style={{flex:1}}><div className="prog-fill prog-fill-gradient" style={{width:prog+"%"}}/></div>
              <span style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:700,color:"var(--muted2)"}}>{prog}%</span>
            </div>
            <div style={{display:"flex",gap:16,marginTop:8}}>
              <span style={{fontFamily:"var(--font-body)",fontSize:12,color:"var(--muted)"}}>🏁 {pt.length} tasks</span>
              <span style={{fontFamily:"var(--font-body)",fontSize:12,color:"var(--muted)"}}>✅ {pt.filter(t=>t.status==="complete").length} done</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── GANTT ────────────────────────────────────────────────────────────────────
function GanttPage({projects,tasks,deps,implied,selProj,setSelProj,users,projTasks,projDeps}){
  const [zoom,setZoom]=useState(28);
  const [showCP,setShowCP]=useState(false);
  const [filterProj,setFilterProj]=useState(selProj||"all");

  const visibleTasks=filterProj==="all"?tasks:tasks.filter(t=>t.projectId===Number(filterProj));
  const visibleDeps =filterProj==="all"?deps:projDeps(Number(filterProj));
  const criticalSet =useMemo(()=>showCP?computeCriticalPath(visibleTasks,visibleDeps,implied):new Set(),[showCP,visibleTasks,visibleDeps,implied]);

  const {minDate,totalDays,colDates}=useMemo(()=>{
    if(!visibleTasks.length)return{minDate:new Date(),totalDays:90,colDates:[]};
    let mn=null,mx=null;
    visibleTasks.forEach(t=>{
      const i=implied[t.id];
      const s=i?i.impliedStart:parseDate(t.startDate); const e=i?i.impliedFinish:parseDate(t.finishDate);
      const ps=parseDate(t.startDate),pe=parseDate(t.finishDate);
      if(!mn||s<mn)mn=s; if(!mn||ps<mn)mn=ps;
      if(!mx||e>mx)mx=e; if(!mx||pe>mx)mx=pe;
    });
    mn=addDays(mn,-7); mx=addDays(mx,14);
    const tot=diffDays(mn,mx);
    // Weekly columns — find first Monday on or before minDate
    const weekCols=[];
    const firstMon=new Date(mn);
    firstMon.setDate(firstMon.getDate()-((firstMon.getDay()+6)%7)); // roll back to Monday
    let wc=new Date(firstMon);
    while(wc<=mx){weekCols.push(new Date(wc));wc.setDate(wc.getDate()+7);}
    return{minDate:mn,totalDays:tot,colDates:weekCols};
  },[visibleTasks,implied,zoom]);

  const totalWidth=totalDays*zoom;
  const dayX=d=>diffDays(minDate,d instanceof Date?d:parseDate(d))*zoom;
  const todayX=dayX(new Date());
  const anyVariance=visibleTasks.some(t=>{const i=implied[t.id];return i&&(fmtDate(i.impliedStart)!==t.startDate||fmtDate(i.impliedFinish)!==t.finishDate);});

  // Month labels — derived from weekly cols for the header band above week ticks
  const monthLabels=useMemo(()=>{
    const seen=new Set(); const labels=[];
    colDates.forEach(d=>{
      const key=d.getFullYear()+"-"+d.getMonth();
      if(!seen.has(key)){seen.add(key);labels.push({date:new Date(d.getFullYear(),d.getMonth(),1),label:d.toLocaleDateString("en-GB",{month:"short",year:"2-digit"})});}
    });
    return labels;
  },[colDates]);

  return(
    <div className="fade-up gantt-section">
      <div className="page-hdr"><h2>Gantt Chart</h2></div>
      <div className="gantt-wrap">
        <div className="gantt-toolbar">
          <select value={filterProj} onChange={e=>{setFilterProj(e.target.value);setSelProj(e.target.value==='all'?null:Number(e.target.value));}}
            style={{background:"var(--surface2)",border:"1px solid var(--border2)",borderRadius:8,padding:"6px 10px",color:"var(--text)",fontFamily:"var(--font-ui)",fontSize:10,letterSpacing:".06em"}}>
            <option value="all">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div style={{display:"flex",gap:4}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>setZoom(z=>Math.max(8,z-8))}>−</button>
            <span style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--muted)",padding:"0 4px",lineHeight:"28px",letterSpacing:".06em"}}>{zoom}px/day</span>
            <button className="btn btn-ghost btn-sm" onClick={()=>setZoom(z=>Math.min(60,z+8))}>+</button>
          </div>
          <button className={`btn btn-cp btn-sm ${showCP?"active":""}`} onClick={()=>setShowCP(v=>!v)}>
            {showCP?"◉":"○"} Critical Path
          </button>
          <div style={{display:"flex",alignItems:"center",gap:14,marginLeft:8}}>
            {anyVariance&&<div style={{display:"flex",alignItems:"center",gap:5,fontFamily:"var(--font-ui)",fontSize:9,color:"var(--muted)",letterSpacing:".06em"}}>
              <div style={{width:20,height:7,borderRadius:2,background:"var(--pink)",opacity:.25}}/> Planned
            </div>}
            <div style={{display:"flex",alignItems:"center",gap:5,fontFamily:"var(--font-ui)",fontSize:9,color:"var(--muted)",letterSpacing:".06em"}}>
              <div style={{width:20,height:7,borderRadius:2,background:"linear-gradient(90deg,var(--pink),var(--teal))",backgroundImage:"linear-gradient(90deg,#ff6eb4,#40e0d0)"}}/> {anyVariance?"Implied":"Scheduled"}
            </div>
            {showCP&&<div style={{display:"flex",alignItems:"center",gap:5,fontFamily:"var(--font-ui)",fontSize:9,color:"var(--cp)",letterSpacing:".06em"}}>
              <div style={{width:20,height:7,borderRadius:2,background:"var(--cp)"}}/> Critical
            </div>}
          </div>
        </div>
        {visibleTasks.length===0&&(
          <div style={{padding:"0 16px 16px"}}>
            <EmptyState
              message="No signal on this frequency. Select a project or add some tasks."
              sub="Use the dropdown above to filter or switch projects"/>
          </div>
        )}
        {visibleTasks.length>0&&<div className="gantt-body" style={{maxHeight:"calc(100vh - 220px)"}}>
          <div className="gantt-labels">
            <div className="gantt-labels-header">Task</div>
            {visibleTasks.map(t=>{
              const proj=projects.find(p=>p.id===t.projectId);
              const isCp=showCP&&criticalSet.has(t.id);
              return(
                <div key={t.id} className="gantt-label-row" style={{borderLeft:isCp?"3px solid var(--cp)":"3px solid transparent"}}>
                  {proj&&filterProj==="all"&&<div style={{width:7,height:7,borderRadius:"50%",background:proj.color,flexShrink:0,boxShadow:"0 0 4px "+proj.color}}/>}
                  {isCp&&<div className="cp-badge"/>}
                  <span className="gantt-label-text" style={{color:t.status==="complete"?"var(--muted)":"var(--text)"}}>{t.name}</span>
                </div>
              );
            })}
          </div>
          <div className="gantt-chart">
            <div className="gantt-canvas-wrap" style={{width:totalWidth+40}}>
              {/* Month band */}
              <div style={{position:"relative",height:22,width:totalWidth+40,borderBottom:"1px solid var(--border)",background:"var(--surface)"}}>
                {monthLabels.map((m,i)=>(
                  <div key={i} style={{position:"absolute",left:Math.max(0,dayX(m.date)),top:0,height:22,
                    display:"flex",alignItems:"center",paddingLeft:6,
                    fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".1em",
                    textTransform:"uppercase",color:"var(--pink)",whiteSpace:"nowrap"}}>
                    {m.label}
                  </div>
                ))}
              </div>
              {/* Week tick labels */}
              <div className="gantt-header-row" style={{width:totalWidth+40,height:22}}>
                {colDates.map((d,i)=>(
                  <div key={i} className="gantt-col-label" style={{left:dayX(d),fontSize:8}}>
                    {d.toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                  </div>
                ))}
              </div>
              {/* Gridlines + today line */}
              <div style={{position:"absolute",top:44,left:0,right:0,bottom:0,pointerEvents:"none"}}>
                {colDates.map((d,i)=>(
                  <div key={i} className="gantt-grid-line" style={{left:dayX(d),opacity:.3}}/>
                ))}
                {monthLabels.map((m,i)=>(
                  <div key={"m"+i} style={{position:"absolute",top:0,bottom:0,left:Math.max(0,dayX(m.date)),width:1,background:"var(--border2)",opacity:.9}}/>
                ))}
                {todayX>0&&todayX<totalWidth&&<div className="gantt-today-line" style={{left:todayX}}/>}
              </div>
              <div style={{position:"relative"}}>
                {visibleTasks.map(t=>{
                  const impl=implied[t.id];
                  const isCp=showCP&&criticalSet.has(t.id);
                  const proj=projects.find(p=>p.id===t.projectId);
                  const barColor=isCp?"var(--cp)":proj?proj.color:"var(--pink)";
                  const pStart=dayX(t.startDate),pWidth=Math.max(4,dayX(t.finishDate)-pStart);
                  const iStart=impl?dayX(impl.impliedStart):pStart;
                  const iWidth=impl?Math.max(4,dayX(impl.impliedFinish)-iStart):pWidth;
                  const hasVariance=impl&&(fmtDate(impl.impliedStart)!==t.startDate||fmtDate(impl.impliedFinish)!==t.finishDate);
                  const taskDeps=visibleDeps.filter(d=>d.successorId===t.id&&implied[d.predecessorId]);
                  return(
                    <div key={t.id} className="gantt-task-row" style={{width:totalWidth+40}}>
                      {hasVariance&&<div className="gantt-bar gantt-bar-planned" style={{left:pStart,width:pWidth,background:barColor}}/>}
                      <div className={`gantt-bar gantt-bar-implied${isCp?" gantt-bar-cp":""}`}
                        style={{left:iStart,width:iWidth,background:`linear-gradient(90deg,${barColor},${barColor}cc)`}}/>
                      {taskDeps.map(dep=>{
                        const pi=implied[dep.predecessorId];
                        if(!pi||!dep.lagDays)return null;
                        return<div key={dep.id} className="gantt-lag" style={{left:dayX(pi.impliedFinish),width:dep.lagDays*zoom}}/>;
                      })}
                      {t.status==="complete"&&<div style={{position:"absolute",left:iStart+iWidth+4,top:"50%",transform:"translateY(-50%)",fontFamily:"var(--font-ui)",fontSize:9,color:"var(--teal)"}}>✓</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}

// ─── TEAM PAGE ────────────────────────────────────────────────────────────────
function TeamPage({users,tasks,implied,user}){
  return(
    <div className="fade-up">
      <div className="page-hdr"><h2>Team</h2></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
        {users.map(u=>{
          const open=tasks.filter(t=>t.status!=="complete"&&t.assigneeId===u.id);
          const done=tasks.filter(t=>t.status==="complete"&&t.assigneeId===u.id);
          const overdue=open.filter(t=>{const i=implied[t.id];return i&&i.impliedFinish<new Date();});
          return(
            <div key={u.id} className="card" style={{borderLeftColor:u.color}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <div className="av" style={{width:42,height:42,background:u.color+"28",color:u.color,fontSize:16,borderRadius:"50%",boxShadow:"0 0 12px "+u.color+"44"}}>{u.initials}</div>
                <div>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:15,fontWeight:800,letterSpacing:".06em"}}>{u.name}</div>
                  <div style={{fontFamily:"var(--font-body)",fontSize:12,color:"var(--muted)"}}>{u.email}</div>
                </div>
                {u.id===user.id&&<div className="badge badge-active" style={{marginLeft:"auto"}}>You</div>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                {[["Open",open.length,"var(--pink)"],["Done",done.length,"var(--teal)"],["Overdue",overdue.length,"var(--danger)"]].map(([lbl,val,clr])=>(
                  <div key={lbl} style={{background:"var(--surface2)",borderRadius:8,padding:"10px 12px"}}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:22,fontWeight:800,color:clr}}>{val}</div>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:9,letterSpacing:".08em",color:"var(--muted)",marginTop:3,textTransform:"uppercase"}}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── COMMENTS TAB ────────────────────────────────────────────────────────────
function CommentsTab({task,users,currentUser,onAddComment}){
  const [text,setText]=useState("");
  const comments=[...(task.comments||[])].reverse(); // newest first

  const post=()=>{
    const trimmed=text.trim();
    if(!trimmed)return;
    onAddComment(task.id,{
      userId:currentUser.id,
      text:trimmed,
      timestamp:new Date().toISOString()
    });
    setText("");
  };

  return(
    <>
      {/* Comment input */}
      <div className="comment-input-row" style={{marginBottom:14}}>
        <textarea
          value={text}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey))post();}}
          placeholder="Add a comment… (Ctrl+Enter to post)"/>
        <button className="btn btn-primary btn-sm"
          style={{flexShrink:0,alignSelf:"flex-end",padding:"9px 14px"}}
          onClick={post}>Post</button>
      </div>

      {/* Comment log */}
      {comments.length===0&&(
        <div style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",textAlign:"center",padding:"20px 0"}}>
          No comments yet — be the first to add one.
        </div>
      )}
      <div className="comment-log">
        {comments.map((c,i)=>{
          const author=users.find(u=>u.id===c.userId)||{initials:"?",color:"var(--muted)",name:"Unknown"};
          return(
            <div key={i} className="comment-item">
              <div className="comment-meta">
                <div className="comment-av" style={{background:author.color+"28",color:author.color}}>
                  {author.initials}
                </div>
                <span className="comment-who" style={{color:author.color}}>{author.initials}</span>
                <span className="comment-sep">·</span>
                <span className="comment-date">{fmtComment(c.timestamp)}</span>
                <span className="comment-sep">—</span>
              </div>
              <div className="comment-text">{c.text}</div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── MODALS ───────────────────────────────────────────────────────────────────
function ModalRouter({modal,setModal,projects,tasks,deps,users,user,addProject,addTask,updateTask,addDep,removeDep,updateLag,onAddComment}){
  const close=()=>setModal(null);
  if(modal.type==="project")  return<ProjectModal  close={close} user={user} addProject={addProject}/>;
  if(modal.type==="task")     return<TaskModal     close={close} user={user} users={users} projectId={modal.projectId} addTask={addTask}/>;
  if(modal.type==="editTask"){
    // Always look up the live task from state — prevents crash when tasks array updates while modal is open
    const liveTask=tasks.find(t=>t.id===modal.taskId);
    if(!liveTask)return null; // task no longer exists — close gracefully
    return<EditTaskModal close={close} task={liveTask} users={users} updateTask={updateTask} tasks={tasks} deps={deps} addDep={addDep} removeDep={removeDep} updateLag={updateLag} currentUser={user} onAddComment={onAddComment}/>;
  }
  if(modal.type==="deps"){
    const liveTask=tasks.find(t=>t.id===modal.taskId);
    if(!liveTask)return null;
    return<DepsModal close={close} task={liveTask} tasks={tasks} deps={deps} addDep={addDep} removeDep={removeDep} updateLag={updateLag}/>;
  }
  return null;
}

function ProjectModal({close,user,addProject}){
  const [f,setF]=useState({name:'',description:'',status:'not-started',color:'#ff6eb4'});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const colors=['#ff6eb4','#40e0d0','#b06eff','#ff6b6b','#ffaa55','#00ccff','#da70d6','#aaff00','#cc2266','#88eeff'];
  const doAdd=()=>{if(!f.name.trim())return;addProject(f);close();};
  return(
    <div className='overlay' onClick={e=>e.target===e.currentTarget&&close()}>
      <div className='modal fade-up'>
        <h3>New Project</h3>
        <div className='field'><label>Name</label>
          <input value={f.name} onChange={e=>set('name',e.target.value)} placeholder='e.g. Bathroom Remodel' autoFocus/>
        </div>
        <div className='field'><label>Description</label>
          <textarea value={f.description} onChange={e=>set('description',e.target.value)} placeholder='What is this project about?'/>
        </div>
        <div className='field'><label>Status</label>
          <select value={f.status} onChange={e=>set('status',e.target.value)}>
            <option value='not-started'>Not Started</option>
            <option value='in-progress'>In Progress</option>
            <option value='stuck'>Stuck</option>
            <option value='complete'>Complete</option>
          </select>
        </div>
        <div className='field'><label>Colour</label>
          <div style={{display:'flex',gap:8,marginTop:4,flexWrap:'wrap'}}>
            {colors.map(c=>(
              <div key={c} onClick={()=>set('color',c)}
                style={{width:24,height:24,borderRadius:'50%',background:c,cursor:'pointer',
                  outline:f.color===c?'2px solid white':'none',outlineOffset:2,
                  boxShadow:'0 0 8px '+c+'88'}}/>
            ))}
          </div>
        </div>
        <div className='modal-foot'>
          <button className='btn btn-ghost' onClick={close}>Cancel</button>
          <button className='btn btn-primary' onClick={doAdd}>Create Project</button>
        </div>
      </div>
    </div>
  );
}

function TaskModal({close,user,users,projectId,addTask}){
  const [f,setF]=useState({name:'',startDate:'',finishDate:'',assigneeId:user.id,projectId,repeatable:false,frequency:'monthly'});
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const doAdd=()=>{
    if(!f.name.trim()||!f.startDate||!f.finishDate)return;
    addTask(f);close();
  };
  return(
    <div className='overlay' onClick={e=>e.target===e.currentTarget&&close()}>
      <div className='modal fade-up'>
        <h3>New Task</h3>
        <div className='field'><label>Task Name</label>
          <input value={f.name} onChange={e=>set('name',e.target.value)} placeholder='e.g. Clean bathroom' autoFocus/>
        </div>
        <div className='row-fields'>
          <div className='field'><label>Planned Start</label>
            <input type='date' value={f.startDate} onChange={e=>set('startDate',e.target.value)}/>
          </div>
          <div className='field'><label>Planned Finish</label>
            <input type='date' value={f.finishDate} onChange={e=>set('finishDate',e.target.value)}/>
          </div>
        </div>
        <div className='field'><label>Assign To</label>
          <select value={f.assigneeId} onChange={e=>set('assigneeId',Number(e.target.value))}>
            {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:'var(--surface2)',borderRadius:10,border:'1px solid var(--border2)',marginBottom:14}}>
          <div style={{flex:1}}>
            <div style={{fontFamily:'var(--font-ui)',fontSize:10,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--muted2)'}}>Make Repeatable</div>
            <div style={{fontFamily:'var(--font-body)',fontSize:11,color:'var(--muted)',marginTop:2}}>Auto-creates next task on completion</div>
          </div>
          <div onClick={()=>set('repeatable',!f.repeatable)}
            style={{width:36,height:20,borderRadius:20,background:f.repeatable?'var(--teal)':'var(--surface3)',border:'1px solid var(--border2)',cursor:'pointer',position:'relative',transition:'background .2s',flexShrink:0}}>
            <div style={{position:'absolute',top:2,left:f.repeatable?18:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'left .2s'}}/>
          </div>
        </div>
        {f.repeatable&&(
          <div className='field'><label>Repeat Frequency</label>
            <select value={f.frequency} onChange={e=>set('frequency',e.target.value)}>
              <option value='weekly'>Weekly</option>
              <option value='monthly'>Monthly</option>
              <option value='yearly'>Yearly</option>
            </select>
          </div>
        )}
        <div className='modal-foot'>
          <button className='btn btn-ghost' onClick={close}>Cancel</button>
          <button className='btn btn-primary' onClick={doAdd}>Create Task</button>
        </div>
      </div>
    </div>
  );
}

function EditTaskModal({close,task,users,updateTask,tasks,deps,addDep,removeDep,updateLag,currentUser,onAddComment}){
  const [f,setF]=useState({
    name:task.name,startDate:task.startDate,finishDate:task.finishDate,
    assigneeId:task.assigneeId,repeatable:task.repeatable||false,frequency:task.frequency||'monthly',
  });
  const [tab,setTab]=useState(0); // mobile only: 0=details+deps 1=comments
  const [newComment,setNewComment]=useState('');
  const [selPred,setSelPred]=useState('');
  const [lag,setLag]=useState(0);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const taskDeps=deps.filter(d=>d.successorId===task.id);
  const eligible=tasks.filter(t=>t.id!==task.id&&t.projectId===task.projectId&&!taskDeps.find(d=>d.predecessorId===t.id));
  const comments=[...(task.comments||[])].reverse();

  const tabBtnStyle=(idx)=>({
    padding:'6px 14px',border:'none',borderRadius:20,cursor:'pointer',
    fontFamily:'var(--font-ui)',fontSize:9,fontWeight:700,letterSpacing:'.08em',
    textTransform:'uppercase',transition:'all .15s',
    background:tab===idx?'var(--pink)':'var(--surface2)',
    color:tab===idx?'#0a0a1a':'var(--muted2)'
  });

  const postComment=()=>{
    const t=newComment.trim();
    if(!t)return;
    onAddComment(task.id,{userId:currentUser.id,text:t,timestamp:new Date().toISOString()});
    setNewComment('');
  };

  const doSave=()=>{
    if(!f.name.trim())return;
    updateTask(task.id,f);
    close();
  };

  // ── Reusable panels ──────────────────────────────────────────────────────
  const DetailsAndDeps=(
    <div>
      {/* Task name */}
      <div className="field"><label>Task Name</label>
        <input value={f.name} onChange={e=>set('name',e.target.value)} autoFocus/>
      </div>
      {/* Dates */}
      <div className="row-fields">
        <div className="field"><label>Planned Start</label>
          <input type="date" value={f.startDate} onChange={e=>set('startDate',e.target.value)}/>
        </div>
        <div className="field"><label>Planned Finish</label>
          <input type="date" value={f.finishDate} onChange={e=>{
            let v=e.target.value;
            if(f.startDate&&v<f.startDate) v=f.startDate;
            set('finishDate',v);
          }}/>
        </div>
      </div>
      {/* Assignee */}
      <div className="field"><label>Assign To</label>
        <select value={f.assigneeId||''} onChange={e=>set('assigneeId',Number(e.target.value))}>
          <option value="">Unassigned</option>
          {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      {/* Status */}
      <div className="field"><label>Status</label>
        <select value={f.status||task.status||'not-started'} onChange={e=>set('status',e.target.value)}>
          {TASK_STATUSES.map(s=><option key={s} value={s}>{taskStatusLabel[s]}</option>)}
        </select>
      </div>
      {/* Repeatable */}
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:'var(--surface2)',borderRadius:10,border:'1px solid var(--border2)',marginBottom:14}}>
        <div style={{flex:1}}>
          <div style={{fontFamily:'var(--font-ui)',fontSize:10,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--muted2)'}}>Repeatable</div>
          <div style={{fontFamily:'var(--font-body)',fontSize:11,color:'var(--muted)',marginTop:2}}>{f.repeatable?(f.frequency||'monthly'):'Off'}</div>
        </div>
        <div onClick={()=>set('repeatable',!f.repeatable)}
          style={{width:36,height:20,borderRadius:20,background:f.repeatable?'var(--teal)':'var(--surface3)',border:'1px solid var(--border2)',cursor:'pointer',position:'relative',transition:'background .2s',flexShrink:0}}>
          <div style={{position:'absolute',top:2,left:f.repeatable?18:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'left .2s'}}/>
        </div>
      </div>
      {f.repeatable&&(
        <div className="field"><label>Frequency</label>
          <select value={f.frequency} onChange={e=>set('frequency',e.target.value)}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      )}
      {/* Dependencies */}
      <div style={{marginTop:18,paddingTop:14,borderTop:'1px solid var(--border)'}}>
        <div style={{fontFamily:'var(--font-ui)',fontSize:10,fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--pink)',marginBottom:10}}>Dependencies</div>
        {taskDeps.length===0&&<p style={{fontFamily:'var(--font-body)',fontSize:12,color:'var(--muted)',marginBottom:10}}>No predecessors set.</p>}
        {taskDeps.map(d=>{
          const pred=tasks.find(t=>t.id===d.predecessorId);
          if(!pred)return null;
          return(
            <div key={d.id} className="dep-item">
              <span className="content-title" style={{fontSize:13,flex:1}}>{pred.name}</span>
              <span style={{fontFamily:'var(--font-ui)',fontSize:9,color:'var(--muted)'}}>LAG:</span>
              <input className="lag-input" type="number" min="0" value={d.lagDays} onChange={e=>updateLag(d.id,e.target.value)}/>
              <span style={{fontFamily:'var(--font-ui)',fontSize:9,color:'var(--muted)'}}>DAYS</span>
              <button className="dep-remove" onClick={()=>removeDep(d.id)}>×</button>
            </div>
          );
        })}
        <div style={{marginTop:10,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <select value={selPred} onChange={e=>setSelPred(e.target.value)}
            style={{flex:1,background:'var(--surface2)',border:'1px solid var(--border2)',borderRadius:8,padding:'6px 10px',color:'var(--text)',fontFamily:'var(--font-body)',fontSize:12}}>
            <option value="">Add predecessor…</option>
            {eligible.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{fontFamily:'var(--font-ui)',fontSize:9,color:'var(--muted)'}}>LAG</span>
            <input className="lag-input" type="number" min="0" value={lag} onChange={e=>setLag(e.target.value)} style={{width:44}}/>
            <span style={{fontFamily:'var(--font-ui)',fontSize:9,color:'var(--muted)'}}>DAYS</span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>{if(!selPred)return;addDep({predecessorId:Number(selPred),successorId:task.id,lagDays:Number(lag)});setSelPred('');setLag(0);}}>Add</button>
        </div>
      </div>
    </div>
  );

  const CommentsPanel=(
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{fontFamily:'var(--font-ui)',fontSize:10,fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--pink)',marginBottom:12}}>
        Comments{task.comments&&task.comments.length>0?' ('+task.comments.length+')':''}
      </div>
      <div className="comment-log" style={{flex:1,overflowY:'auto',maxHeight:340,marginBottom:12}}>
        {comments.length===0&&<div style={{fontFamily:'var(--font-body)',fontSize:12,color:'var(--muted)',textAlign:'center',padding:'20px 0'}}>No comments yet.</div>}
        {comments.map((c,i)=>{
          const author=users.find(u=>u.id===c.userId)||{initials:'?',color:'var(--muted)'};
          return(
            <div key={i} className="comment-item">
              <div className="comment-meta">
                <div className="comment-av" style={{background:author.color+'28',color:author.color}}>{author.initials}</div>
                <span className="comment-who" style={{color:author.color}}>{c.authorName||author.initials}</span>
                <span className="comment-sep">·</span>
                <span className="comment-date">{fmtComment(c.timestamp)}</span>
              </div>
              <div className="comment-text">{c.text}</div>
            </div>
          );
        })}
      </div>
      <div className="comment-input-row">
        <textarea value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Add a comment…" style={{minHeight:60}}/>
        <button className="btn btn-primary btn-sm" style={{flexShrink:0,alignSelf:'flex-end',padding:'8px 14px'}} onClick={postComment}>Post</button>
      </div>
    </div>
  );

  return(
    <div className="overlay" onClick={e=>e.target===e.currentTarget&&close()}>
      <div className="modal modal-wide fade-up">
        <h3 style={{marginBottom:16}}>{task.name}</h3>

        {/* Mobile tabs — hidden on desktop via CSS */}
        <div className="edit-modal-mobile-tabs" style={{display:'flex',gap:6,marginBottom:16}}>
          <button style={tabBtnStyle(0)} onClick={()=>setTab(0)}>Details & Deps</button>
          <button style={tabBtnStyle(1)} onClick={()=>setTab(1)}>Comments{task.comments&&task.comments.length>0?' ('+task.comments.length+')':''}</button>
        </div>

        {/* Desktop: two-panel side by side. Mobile: tab-switched */}
        <div className="edit-modal-body">
          <div className="edit-modal-left" style={{display: tab===0?'block':'none'}}>
            {DetailsAndDeps}
          </div>
          <div className="edit-modal-right" style={{display: tab===1?'flex':'none',flexDirection:'column'}}>
            {CommentsPanel}
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={close}>Cancel</button>
          <button className="btn btn-primary" onClick={doSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}


function DepsModal({close,task,tasks,deps,addDep,removeDep,updateLag}){
  const [selPred,setSelPred]=useState(""); const [lag,setLag]=useState(0);
  const taskDeps=deps.filter(d=>d.successorId===task.id);
  const eligible=tasks.filter(t=>t.id!==task.id&&t.projectId===task.projectId&&!taskDeps.find(d=>d.predecessorId===t.id));
  return(
    <div className="overlay" onClick={e=>e.target===e.currentTarget&&close()}>
      <div className="modal fade-up">
        <h3>Dependencies</h3>
        <p style={{fontFamily:"var(--font-content)",fontSize:18,color:"var(--teal)",marginBottom:12}}>{task.name}</p>
        <p style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",marginBottom:16}}>This task starts after these predecessors finish (Finish-to-Start).</p>
        {taskDeps.length===0&&<p style={{fontFamily:"var(--font-body)",fontSize:13,color:"var(--muted)",marginBottom:12}}>No dependencies yet.</p>}
        {taskDeps.map(d=>{
          const pred=tasks.find(t=>t.id===d.predecessorId);
          return pred?(
            <div key={d.id} className="dep-item">
              <span className="content-title" style={{fontSize:15,flex:1}}>{pred.name}</span>
              <span style={{fontFamily:"var(--font-ui)",fontSize:9,letterSpacing:".06em",color:"var(--muted)"}}>LAG:</span>
              <input className="lag-input" type="number" min="0" value={d.lagDays} onChange={e=>updateLag(d.id,e.target.value)}/>
              <span style={{fontFamily:"var(--font-ui)",fontSize:9,letterSpacing:".06em",color:"var(--muted)"}}>DAYS</span>
              <button className="dep-remove" onClick={()=>removeDep(d.id)}>×</button>
            </div>
          ):null;
        })}
        <div style={{marginTop:16,paddingTop:16,borderTop:"1px solid var(--border)"}}>
          <div style={{fontFamily:"var(--font-ui)",fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:8}}>Add Predecessor</div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <select value={selPred} onChange={e=>setSelPred(e.target.value)} style={{flex:1,background:"var(--surface2)",border:"1px solid var(--border2)",borderRadius:8,padding:"7px 10px",color:"var(--text)",fontFamily:"var(--font-body)",fontSize:13}}>
              <option value="">Select task…</option>
              {eligible.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontFamily:"var(--font-ui)",fontSize:9,letterSpacing:".08em",color:"var(--muted)"}}>LAG</span>
              <input className="lag-input" type="number" min="0" value={lag} onChange={e=>setLag(e.target.value)} style={{width:54}}/>
              <span style={{fontFamily:"var(--font-ui)",fontSize:9,letterSpacing:".08em",color:"var(--muted)"}}>DAYS</span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={()=>{if(!selPred)return;addDep({predecessorId:Number(selPred),successorId:task.id,lagDays:Number(lag)});setSelPred('');setLag(0);}}>Add</button>
          </div>
        </div>
        <div className="modal-foot"><button className="btn btn-ghost" onClick={close}>Done</button></div>
      </div>
    </div>
  );
}
